/*
 * Baby Elephant Customer Journey PRO v5.0.0 ULTIMATE
 * Shopify storefront customer intelligence + journey/friction diagnostics
 *
 * PRIVACIDAD: no captura nombres, correos, teléfonos, direcciones ni valores de formularios.
 * El término del buscador interno se registra solo si no parece contener email/teléfono/RUT
 * y puede desactivarse con BE_JOURNEY_CONFIG.captureSearchTerms = false.
 * Rutas y referrers se guardan sin query strings; UTMs/IDs publicitarios van en campos dedicados.
 */

(() => {
  'use strict';

  const CONFIG = window.BE_JOURNEY_CONFIG || {};
  const VERSION = '5.0.0-customer-intelligence-ultimate';
  const STORAGE_KEY = 'be_journey_v3';
  const PENDING_CLICKS_KEY = 'be_journey_pending_clicks_v3';
  const SESSION_TIMEOUT_MS = (CONFIG.sessionTimeoutMinutes || 30) * 60 * 1000;
  const MAX_SESSIONS = CONFIG.maxSessions || 5;
  const MAX_IMPORTANT_PAGES = CONFIG.maxImportantPages || 30;
  const MAX_LOCAL_EVENTS = CONFIG.maxEventsPerSession || 160;
  const MAX_FIREBASE_CLICKS = 0;
  const HEARTBEAT_MS = 10_000;
  const FIREBASE_SYNC_DELAY_MS = 1_500;
  const CART_SYNC_DELAY_MS = 2_500;
  const DEBUG = Boolean(CONFIG.debug);
  const PRIVATE_ORDER_ATTRIBUTES = CONFIG.privateOrderAttributes !== false;
  const CAPTURE_SEARCH_TERMS = CONFIG.captureSearchTerms !== false;
  const RAW_PRIVATE_ATTRIBUTE_PREFIX = String(CONFIG.privateAttributePrefix || '__CJ').trim() || '__CJ';
  const PRIVATE_ATTRIBUTE_PREFIX = RAW_PRIVATE_ATTRIBUTE_PREFIX.startsWith('__')
    ? RAW_PRIVATE_ATTRIBUTE_PREFIX
    : `__${RAW_PRIVATE_ATTRIBUTE_PREFIX.replace(/^_+/, '')}`;
  const ATTRIBUTE_VALUE_MAX = Math.min(Math.max(Number(CONFIG.attributeValueMax || 235), 120), 245);
  const DRIVER_WINDOW_MS = Math.min(Math.max(Number(CONFIG.driverWindowMinutes || 5), 1), 15) * 60 * 1000;
  const MAX_STAGE_HISTORY = 12;

  const originalFetch = window.fetch ? window.fetch.bind(window) : null;
  const nowIso = () => new Date().toISOString();
  const nowMs = () => Date.now();

  let state = loadState();
  let currentSession = null;
  let firebaseDb = null;
  let firebaseUid = null;
  let firebaseReady = false;
  let firebaseSyncTimer = null;
  let cartSyncTimer = null;
  let cartSnapshot = null;
  let lastTickAt = nowMs();
  let lastScrollCheckAt = 0;
  let pageViewStartedAt = nowMs();
  let rageClicks = [];
  let internalCartUpdate = false;
  let destroyed = false;
  let firebaseCleanupDone = false;
  let clickFlushInProgress = false;

  function log(...args) {
    if (DEBUG) console.log('[BE Journey]', ...args);
  }

  function warn(...args) {
    console.warn('[BE Journey]', ...args);
  }

  function safeParse(value, fallback) {
    try {
      return JSON.parse(value) ?? fallback;
    } catch (_) {
      return fallback;
    }
  }

  function safeString(value, maxLength = 160) {
    return String(value ?? '')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, maxLength);
  }

  function safeSearchTerm(value) {
    if (!CAPTURE_SEARCH_TERMS) return '';
    const term = safeString(value, 80);
    if (!term) return '';
    const compact = term.replace(/\s+/g, ' ').trim();
    const looksEmail = /\b[^\s@]+@[^\s@]+\.[^\s@]+\b/i.test(compact);
    const looksPhone = /(?:\+?56\s*)?(?:9\s*)?\d(?:[\s.-]*\d){7,}/.test(compact);
    const looksRut = /\b\d{1,2}(?:\.\d{3}){2}-[\dkK]\b|\b\d{7,8}-[\dkK]\b/.test(compact);
    if (looksEmail || looksPhone || looksRut) return '';
    return compact;
  }

  function safeReferrer() {
    if (!document.referrer) return '';
    try {
      const url = new URL(document.referrer);
      return `${url.origin}${url.pathname}`.slice(0, 300);
    } catch (_) {
      return '';
    }
  }

  function cleanObject(value) {
    if (Array.isArray(value)) {
      return value
        .map(cleanObject)
        .filter((item) => item !== undefined && item !== null && item !== '');
    }

    if (value && typeof value === 'object') {
      return Object.fromEntries(
        Object.entries(value)
          .map(([key, item]) => [key, cleanObject(item)])
          .filter(([, item]) => item !== undefined && item !== null && item !== '')
      );
    }

    if (typeof value === 'number' && !Number.isFinite(value)) return 0;
    return value;
  }

  function generateId(prefix = 'id') {
    if (window.crypto?.randomUUID) return `${prefix}_${window.crypto.randomUUID()}`;
    return `${prefix}_${nowMs().toString(36)}_${Math.random().toString(36).slice(2, 12)}`;
  }

  function loadState() {
    const saved = safeParse(localStorage.getItem(STORAGE_KEY), null);

    if (saved && Array.isArray(saved.sessions)) {
      return {
        ...saved,
        version: VERSION,
        visitor_id: saved.visitor_id || generateId('visitor'),
        created_at: saved.created_at || nowIso(),
        updated_at: nowIso(),
        first_touch: saved.first_touch || null,
        first_marketing_touch: saved.first_marketing_touch || null,
        last_touch: saved.last_touch || null,
        sessions: saved.sessions.slice(-MAX_SESSIONS).map((session) => ({
          ...session,
          page_active_seconds: session.page_active_seconds || {},
          stage_history: Array.isArray(session.stage_history) && session.stage_history.length
            ? session.stage_history.slice(-MAX_STAGE_HISTORY)
            : [{ stage: 'discovery', timestamp: session.started_at || nowIso() }],
          performance: {
            ttfb_ms: 0,
            fcp_ms: 0,
            lcp_ms: 0,
            cls: 0,
            inp_ms: 0,
            ...(session.performance || {}),
          },
          performance_worst: session.performance_worst || {},
          peak_cart_total: Number(session.peak_cart_total || session.cart?.total_price || 0),
          peak_cart_items: Number(session.peak_cart_items || session.cart?.item_count || 0),
          removed_cart_value: Number(session.removed_cart_value || 0),
        })),
        current_session_id: saved.current_session_id || null,
      };
    }

    return {
      version: VERSION,
      visitor_id: generateId('visitor'),
      created_at: nowIso(),
      updated_at: nowIso(),
      first_touch: null,
      first_marketing_touch: null,
      last_touch: null,
      sessions: [],
      current_session_id: null,
    };
  }

  function saveState() {
    state.updated_at = nowIso();
    state.sessions = state.sessions.slice(-MAX_SESSIONS);

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      warn('No fue posible guardar localStorage:', error);
    }
  }

  function getUrlParams() {
    return new URLSearchParams(window.location.search);
  }

  function getParam(name) {
    return safeString(getUrlParams().get(name), 200);
  }

  function referrerHost() {
    if (!document.referrer) return '';
    try {
      return new URL(document.referrer).hostname.toLowerCase();
    } catch (_) {
      return '';
    }
  }

  function detectSource() {
    const utmSource = getParam('utm_source').toLowerCase();
    if (utmSource) return utmSource;
    if (getParam('gclid')) return 'google';
    if (getParam('fbclid')) return 'meta';
    if (getParam('ttclid')) return 'tiktok';
    if (getParam('msclkid')) return 'bing';

    const refHost = referrerHost();
    const ownHost = window.location.hostname.toLowerCase();

    if (!refHost || refHost === ownHost || refHost.endsWith(`.${ownHost}`)) return 'direct';
    if (refHost.includes('google.')) return 'google-organic';
    if (refHost.includes('bing.')) return 'bing-organic';
    if (refHost.includes('facebook.') || refHost.includes('instagram.')) return 'meta-organic';
    if (refHost.includes('tiktok.')) return 'tiktok-organic';
    return 'referral';
  }

  function detectTouch() {
    const source = detectSource();

    const touch = cleanObject({
      source,
      medium: getParam('utm_medium'),
      platform: getParam('utm_platform'),
      placement: getParam('utm_placement'),
      format: getParam('utm_format'),
      campaign: getParam('utm_campaign'),
      campaign_id: getParam('utm_campaign_id'),
      adset: getParam('utm_adset'),
      adset_id: getParam('utm_adset_id'),
      ad: getParam('utm_ad'),
      ad_id: getParam('utm_ad_id'),
      angle: getParam('utm_angle'),
      hook: getParam('utm_hook'),
      gclid: getParam('gclid'),
      fbclid: getParam('fbclid'),
      ttclid: getParam('ttclid'),
      msclkid: getParam('msclkid'),
      referrer: safeReferrer(),
      landing_path: currentPathWithQuery(),
      timestamp: nowIso(),
    });

    touch.is_marketing = Boolean(
      source !== 'direct' ||
      touch.campaign ||
      touch.ad ||
      touch.gclid ||
      touch.fbclid ||
      touch.ttclid ||
      touch.msclkid
    );

    return touch;
  }

  function updateTouches() {
    const touch = detectTouch();

    if (!state.first_touch) state.first_touch = touch;
    if (touch.is_marketing && !state.first_marketing_touch) {
      state.first_marketing_touch = touch;
    }

    const isExternalEntry =
      touch.is_marketing ||
      Boolean(touch.referrer && referrerHost() !== window.location.hostname);

    if (isExternalEntry || !state.last_touch) {
      state.last_touch = touch;
    }
  }

  function currentPathWithQuery() {
    return safeString(window.location.pathname || '/', 220) || '/';
  }

  function pageCategory() {
    const configured = safeString(CONFIG.page?.type, 50).toLowerCase();
    if (configured) return configured;

    const path = window.location.pathname;

    if (path.includes('/products/')) return 'product';
    if (path.includes('/collections/')) return 'collection';
    if (path.includes('/search')) return 'search';
    if (path.includes('/cart')) return 'cart';
    if (path === '/') return 'index';

    return 'page';
  }

  function importantPage() {
    return [
      'product',
      'collection',
      'search',
      'cart',
      'checkout',
      'index',
    ].includes(pageCategory());
  }

  function deviceInfo() {
    const width =
      window.innerWidth ||
      document.documentElement.clientWidth ||
      0;

    let category = 'desktop';

    if (width < 750) category = 'mobile';
    else if (width < 990) category = 'tablet';

    return {
      category,
      viewport_width: width,
      viewport_height: window.innerHeight || 0,
      screen_width: window.screen?.width || 0,
      screen_height: window.screen?.height || 0,
      pixel_ratio: window.devicePixelRatio || 1,
      language: navigator.language || '',
      timezone:
        Intl.DateTimeFormat().resolvedOptions().timeZone || '',
    };
  }

  function newSession() {
    const touch = detectTouch();

    const session = {
      id: generateId('session'),
      started_at: nowIso(),
      last_activity_at: nowIso(),
      ended_at: null,

      entry_path: currentPathWithQuery(),
      exit_path: currentPathWithQuery(),
      entry_touch: touch,
      device: deviceInfo(),

      page_count: 0,
      important_pages: [],
      products: {},
      collections: [],
      searches: [],
      events: [],

      active_seconds: 0,
      max_scroll_percent: 0,
      click_count: 0,
      clicks_saved: 0,

      score: 0,
      label: 'Frío',
      reasons: [],

      page_active_seconds: {},

      stage_history: [
        {
          stage: 'discovery',
          timestamp: nowIso(),
        },
      ],

      performance: {
        ttfb_ms: 0,
        fcp_ms: 0,
        lcp_ms: 0,
        cls: 0,
        inp_ms: 0,
      },

      performance_worst: {},

      peak_cart_total: 0,
      peak_cart_items: 0,
      removed_cart_value: 0,

      cart: {
        item_count: 0,
        total_price: 0,
        items: [],
      },
    };

    state.sessions.push(session);
    state.sessions = state.sessions.slice(-MAX_SESSIONS);
    state.current_session_id = session.id;

    return session;
  }

  function getCurrentSession() {
    const session = state.sessions.find(
      (item) => item.id === state.current_session_id
    );

    const lastActivity = session
      ? Date.parse(session.last_activity_at)
      : 0;

    const expired =
      !lastActivity ||
      nowMs() - lastActivity > SESSION_TIMEOUT_MS;

    if (!session || expired) {
      if (session && !session.ended_at) {
        session.ended_at = session.last_activity_at;
      }

      return newSession();
    }

    return session;
  }

  function touchSession() {
    currentSession.last_activity_at = nowIso();
    currentSession.exit_path = currentPathWithQuery();
  }

  function eventExists(type) {
    return currentSession.events.some(
      (event) => event.type === type
    );
  }

  function deriveStageCode(session = currentSession) {
    if (!session) return 'discovery';

    const events = session.events || [];
    const has = (type) =>
      events.some((event) => event.type === type);

    const cartItems = Number(
      session.cart?.item_count || 0
    );

    const products =
      Object.keys(session.products || {}).length;

    const hasSearch =
      (session.searches || []).length > 0 ||
      has('search_submit');

    if (has('purchase_detected')) return 'purchase';
    if (has('checkout_click')) return 'checkout';

    if (
      cartItems > 0 ||
      has('add_to_cart')
    ) {
      return 'cart';
    }

    if (
      products &&
      (
        hasSearch ||
        products >= 2 ||
        has('variant_interaction') ||
        has('info_interest')
      )
    ) {
      return 'evaluation';
    }

    if (products) return 'consideration';

    return 'discovery';
  }

  function stageLabel(stage) {
    const labels = {
      discovery: 'Descubrimiento',
      consideration: 'Consideración',
      evaluation: 'Evaluación',
      cart: 'Carrito',
      checkout: 'Checkout',
      purchase: 'Compra confirmada',
    };

    return labels[stage] || 'Descubrimiento';
  }

  function updateStageHistory() {
    if (!currentSession) return;

    currentSession.stage_history =
      Array.isArray(currentSession.stage_history)
        ? currentSession.stage_history
        : [];

    const next = deriveStageCode(currentSession);

    const last =
      currentSession.stage_history[
        currentSession.stage_history.length - 1
      ];

    if (!last || last.stage !== next) {
      currentSession.stage_history.push({
        stage: next,
        timestamp: nowIso(),
      });

      currentSession.stage_history =
        currentSession.stage_history.slice(
          -MAX_STAGE_HISTORY
        );
    }
  }

  function recalculateIntent() {
    const products =
      Object.values(currentSession.products || {});

    const uniqueProducts = products.length;

    const totalProductViews =
      products.reduce(
        (sum, item) => sum + (item.views || 0),
        0
      );

    const eventTypes = new Set(
      currentSession.events.map(
        (event) => event.type
      )
    );

    let score = 0;
    const reasons = [];

    score += Math.min(
      currentSession.page_count * 2,
      10
    );

    if (uniqueProducts) {
      score += Math.min(
        uniqueProducts * 6,
        24
      );

      reasons.push(
        `Vio ${uniqueProducts} producto${
          uniqueProducts === 1 ? '' : 's'
        }`
      );
    }

    if (totalProductViews > uniqueProducts) {
      score += Math.min(
        (totalProductViews - uniqueProducts) * 2,
        10
      );

      reasons.push('Repitió productos');
    }

    if (currentSession.collections.length) {
      score += Math.min(
        currentSession.collections.length * 2,
        6
      );

      reasons.push('Exploró colecciones');
    }

    if (currentSession.searches.length) {
      score += Math.min(
        currentSession.searches.length * 5,
        10
      );

      reasons.push('Realizó búsquedas');
    }

    if (currentSession.active_seconds >= 60) {
      score += 5;
      reasons.push(
        'Permaneció más de 1 minuto'
      );
    }

    if (currentSession.active_seconds >= 180) {
      score += 8;
    }

    if (currentSession.active_seconds >= 600) {
      score += 10;
    }

    if (currentSession.max_scroll_percent >= 75) {
      score += 5;
    }

    if (eventTypes.has('cart_view')) {
      score += 10;
      reasons.push('Visitó el carrito');
    }

    if (eventTypes.has('add_to_cart')) {
      score += 25;
      reasons.push('Agregó al carrito');
    }

    if (eventTypes.has('checkout_click')) {
      score += 30;
      reasons.push('Avanzó al checkout');
    }

    if (eventTypes.has('whatsapp_click')) {
      score += 15;
      reasons.push('Abrió WhatsApp');
    }

    if (eventTypes.has('showroom_click')) {
      score += 15;
      reasons.push(
        'Mostró interés en showroom'
      );
    }

    if (
      eventTypes.has('discount_interaction')
    ) {
      score += 5;
    }

    if (eventTypes.has('rage_click')) {
      reasons.push('Tuvo clics repetitivos');
    }

    if (
      eventTypes.has('cart_request_error')
    ) {
      reasons.push(
        'Tuvo un error en una operación de carrito'
      );
    }

    if (
      eventTypes.has('add_to_cart_failed')
    ) {
      reasons.push(
        'Intentó agregar sin confirmación de carrito'
      );
    }

    if (
      eventTypes.has('checkout_stalled')
    ) {
      reasons.push(
        'Intentó avanzar y permaneció en carrito'
      );
    }

    if (state.sessions.length > 1) {
      score += 10;
      reasons.push('Cliente recurrente');
    }

    if (
      eventTypes.has('remove_from_cart')
    ) {
      score -= 5;
    }

    score = Math.max(
      0,
      Math.min(100, Math.round(score))
    );

    currentSession.score = score;

    currentSession.label =
      score >= 75
        ? 'Muy caliente'
        : score >= 50
          ? 'Caliente'
          : score >= 25
            ? 'Tibio'
            : 'Frío';

    currentSession.reasons =
      [...new Set(reasons)].slice(0, 8);

    updateStageHistory();
  }

  function pushToDataLayer(type, details) {
    if (CONFIG.pushToDataLayer === false) {
      return;
    }

    window.dataLayer =
      window.dataLayer || [];

    window.dataLayer.push({
      event: `be_${type}`,

      be_journey: {
        visitor_id: state.visitor_id,
        session_id: currentSession.id,
        score: currentSession.score,
        label: currentSession.label,
        ...cleanObject(details || {}),
      },
    });
  }

  function sendToClarity(type) {
    try {
      if (
        typeof window.clarity === 'function'
      ) {
        window.clarity(
          'event',
          `be_${type}`
        );

        window.clarity(
          'set',
          'be_intent',
          currentSession.label
        );

        window.clarity(
          'set',
          'be_source',
          preferredTouch()?.source ||
            'direct'
        );
      }
    } catch (_) {
      // Clarity es opcional.
    }
  }

  function trackEvent(
    type,
    details = {},
    options = {}
  ) {
    if (!currentSession) return null;

    const event = cleanObject({
      id: generateId('event'),
      type,
      timestamp: nowIso(),
      path: currentPathWithQuery(),
      page_type: pageCategory(),
      details,
    });

    currentSession.events.push(event);

    currentSession.events =
      currentSession.events.slice(
        -MAX_LOCAL_EVENTS
      );

    touchSession();
    recalculateIntent();
    saveState();

    if (
      options.dataLayer !== false
    ) {
      pushToDataLayer(type, details);
    }

    if (options.clarity !== false) {
      sendToClarity(type);
    }

    scheduleFirebaseSync();

    if (options.cart !== false) {
      scheduleCartSync();
    }

    return event;
  }

  function recordPageView() {
    currentSession.page_count += 1;

    touchSession();

    const page = cleanObject({
      path: currentPathWithQuery(),
      type: pageCategory(),
      title: safeString(
        document.title,
        180
      ),
      timestamp: nowIso(),
    });

    if (importantPage()) {
      const previous =
        currentSession.important_pages[
          currentSession.important_pages.length - 1
        ];

      if (
        !previous ||
        previous.path !== page.path
      ) {
        currentSession.important_pages.push(
          page
        );

        currentSession.important_pages =
          currentSession.important_pages.slice(
            -MAX_IMPORTANT_PAGES
          );
      }
    }

    if (page.type === 'product') {
      const product =
        CONFIG.page?.product || {};

      const key = safeString(
        product.handle ||
          window.location.pathname.split(
            '/products/'
          )[1] ||
          'product',
        120
      );

      const previous =
        currentSession.products[key] || {
          handle: key,
          id: product.id || null,
          title: safeString(
            product.title ||
              document.title,
            180
          ),
          variant_id:
            product.variantId || null,
          price: Number(
            product.price || 0
          ),
          views: 0,
          active_seconds: 0,
          first_viewed_at: nowIso(),
        };

      previous.views += 1;
      previous.last_viewed_at =
        nowIso();

      currentSession.products[key] =
        previous;

      trackEvent(
        'product_view',
        {
          product: previous,
        },
        {
          cart: false,
        }
      );
    } else if (
      page.type === 'collection'
    ) {
      const collection =
        CONFIG.page?.collection || {};

      const entry = {
        handle: safeString(
          collection.handle ||
            window.location.pathname.split(
              '/collections/'
            )[1] ||
            '',
          120
        ),
        title: safeString(
          collection.title ||
            document.title,
          180
        ),
        timestamp: nowIso(),
      };

      if (
        !currentSession.collections.some(
          (item) =>
            item.handle ===
            entry.handle
        )
      ) {
        currentSession.collections.push(
          entry
        );

        currentSession.collections =
          currentSession.collections.slice(
            -20
          );
      }

      trackEvent(
        'collection_view',
        entry,
        {
          cart: false,
        }
      );
    } else if (
      page.type === 'search'
    ) {
      const term = safeSearchTerm(
        CONFIG.page?.searchTerm ||
          getParam('q')
      );

      if (
        term &&
        !currentSession.searches.some(
          (item) => item.term === term
        )
      ) {
        currentSession.searches.push({
          term,
          timestamp: nowIso(),
        });

        currentSession.searches =
          currentSession.searches.slice(
            -20
          );
      }

      trackEvent(
        'search_view',
        {
          term,
        },
        {
          cart: false,
        }
      );
    } else if (
      page.type === 'cart'
    ) {
      trackEvent(
        'cart_view',
        {},
        {
          cart: true,
        }
      );
    } else {
      recalculateIntent();
      saveState();
      scheduleFirebaseSync();
    }
  }

  function tickActiveTime() {
    const current = nowMs();

    const elapsed = Math.max(
      0,
      Math.min(
        15,
        Math.round(
          (current - lastTickAt) /
            1000
        )
      )
    );

    lastTickAt = current;

    if (
      document.visibilityState !==
        'visible' ||
      elapsed === 0
    ) {
      return;
    }

    currentSession.active_seconds +=
      elapsed;

    currentSession.page_active_seconds =
      currentSession.page_active_seconds ||
      {};

    const activePath =
      currentPathWithQuery();

    currentSession.page_active_seconds[
      activePath
    ] =
      Number(
        currentSession
          .page_active_seconds[
          activePath
        ] || 0
      ) + elapsed;

    if (
      pageCategory() === 'product'
    ) {
      const handle = safeString(
        CONFIG.page?.product?.handle ||
          window.location.pathname.split(
            '/products/'
          )[1] ||
          '',
        120
      );

      if (
        handle &&
        currentSession.products[
          handle
        ]
      ) {
        currentSession.products[
          handle
        ].active_seconds += elapsed;
      }
    }

    touchSession();
    recalculateIntent();
    saveState();
    scheduleFirebaseSync();
  }

  function monitorScroll() {
    const current = nowMs();

    if (
      current -
        lastScrollCheckAt <
      400
    ) {
      return;
    }

    lastScrollCheckAt = current;

    const documentHeight =
      Math.max(
        document.body.scrollHeight,
        document.documentElement
          .scrollHeight,
        document.body.offsetHeight,
        document.documentElement
          .offsetHeight
      );

    const viewedBottom =
      window.scrollY +
      window.innerHeight;

    const percent =
      documentHeight > 0
        ? Math.min(
            100,
            Math.round(
              (viewedBottom /
                documentHeight) *
                100
            )
          )
        : 0;

    if (
      percent <=
      currentSession.max_scroll_percent
    ) {
      return;
    }

    const previous =
      currentSession.max_scroll_percent;

    currentSession.max_scroll_percent =
      percent;

    const thresholds = [
      25,
      50,
      75,
      90,
    ];

    thresholds.forEach(
      (threshold) => {
        if (
          previous < threshold &&
          percent >= threshold
        ) {
          trackEvent(
            'scroll_depth',
            {
              percent: threshold,
            },
            {
              cart: false,
              clarity: false,
            }
          );
        }
      }
    );

    saveState();
  }

  function buildSelector(element) {
    if (!(element instanceof Element)) {
      return '';
    }

    if (element.id) {
      return `#${CSS.escape(
        element.id
      )}`;
    }

    const parts = [];
    let node = element;
    let depth = 0;

    while (
      node &&
      node !== document.body &&
      depth < 4
    ) {
      let part =
        node.tagName.toLowerCase();

      const stableClass = [
        ...node.classList,
      ].find(
        (name) =>
          !/^(active|open|focus|hover|loading|animate|is-)/i.test(
            name
          )
      );

      if (stableClass) {
        part += `.${CSS.escape(
          stableClass
        )}`;
      }

      const parent =
        node.parentElement;

      if (parent) {
        const siblings = [
          ...parent.children,
        ].filter(
          (child) =>
            child.tagName ===
            node.tagName
        );

        if (siblings.length > 1) {
          part += `:nth-of-type(${
            siblings.indexOf(node) + 1
          })`;
        }
      }

      parts.unshift(part);
      node = parent;
      depth += 1;
    }

    return parts
      .join(' > ')
      .slice(0, 240);
  }

  function safeHref(element) {
    const anchor =
      element.closest?.('a[href]');

    if (!anchor) return '';

    try {
      const url = new URL(
        anchor.href,
        window.location.origin
      );

      return url.origin ===
        window.location.origin
        ? `${url.pathname}${url.hash}`
        : `${url.origin}${url.pathname}`;
    } catch (_) {
      return '';
    }
  }

  function classifyClick(element) {
    const anchor =
      element.closest?.('a[href]');

    const href = (
      anchor?.href || ''
    ).toLowerCase();

    const text = safeString(
      element.getAttribute?.(
        'aria-label'
      ) ||
        element.getAttribute?.(
          'title'
        ) ||
        element.textContent,
      100
    ).toLowerCase();

    if (
      element.closest?.(
        '[name="add"], [data-type="add-to-cart-form"] button[type="submit"]'
      )
    ) {
      return 'add_to_cart_click';
    }

    if (
      element.closest?.(
        'button[name="checkout"], input[name="checkout"], a[href*="/checkout"]'
      )
    ) {
      return 'checkout_click';
    }

    if (
      /wa\.me|api\.whatsapp\.com|whatsapp/.test(
        href
      ) ||
      text.includes('whatsapp')
    ) {
      return 'whatsapp_click';
    }

    if (
      href.includes(
        '/pages/retiro-en-tienda'
      ) ||
      text.includes('showroom') ||
      text.includes(
        'agenda una visita'
      ) ||
      text.includes(
        'agendar visita'
      )
    ) {
      return 'showroom_click';
    }

    if (
      href.includes('/discount/') ||
      text.includes('cupón') ||
      text.includes('cupon') ||
      text.includes('descuento')
    ) {
      return 'discount_interaction';
    }

    if (
      href.includes('/products/')
    ) {
      return 'product_click';
    }

    if (
      href.includes(
        '/collections/'
      )
    ) {
      return 'collection_click';
    }

    if (
      element.closest?.(
        'summary, .header__menu-item, .menu-drawer__menu-item'
      )
    ) {
      return 'menu_click';
    }

    if (
      element.closest?.(
        'button[type="submit"]'
      ) &&
      element.closest?.(
        'form[action*="/search"]'
      )
    ) {
      return 'search_submit';
    }

    if (anchor) return 'link_click';

    if (
      element.closest?.(
        'button, [role="button"], summary'
      )
    ) {
      return 'button_click';
    }

    return 'space_click';
  }

  function currentProductName() {
    const product =
      CONFIG.page?.product || {};

    if (
      pageCategory() !== 'product'
    ) {
      return '';
    }

    return cleanPageTitle(
      product.title ||
        document.title ||
        product.handle ||
        ''
    );
  }

  function interactionContextText(
    element
  ) {
    if (!(element instanceof Element)) {
      return '';
    }

    const summary =
      element.closest?.('summary');

    const details =
      element.closest?.('details');

    const fieldset =
      element.closest?.('fieldset');

    const label =
      element.closest?.('label');

    const container =
      element.closest?.(
        '[class*="accordion"], [class*="collapsible"], [class*="drawer"], [class*="modal"], [class*="product__info"], [class*="product-info"]'
      );

    return safeString(
      [
        element.getAttribute?.(
          'aria-label'
        ),
        element.getAttribute?.(
          'title'
        ),
        element.getAttribute?.(
          'name'
        ),
        element.getAttribute?.('id'),
        element.textContent,
        summary?.textContent,
        details?.querySelector?.(
          'summary'
        )?.textContent,
        fieldset?.querySelector?.(
          'legend'
        )?.textContent,
        label?.textContent,
        container?.getAttribute?.(
          'aria-label'
        ),
        element
          .closest?.('a[href]')
          ?.getAttribute?.('href'),
      ]
        .filter(Boolean)
        .join(' '),
      420
    ).toLowerCase();
  }

  function detectInfoTopic(element) {
    const text =
      interactionContextText(element);

    if (!text) return '';

    const rules = [
      [
        'medidas/compatibilidad',
        /(medida|medidas|dimensi[oó]n|dimensiones|tama[nñ]o|ancho|alto|largo|cent[ií]metro|\bcm\b|compatib|extensi[oó]n|ajustable)/i,
      ],
      [
        'despacho/entrega',
        /(despacho|env[ií]o|envio|entrega|delivery|retiro|shipping|llega hoy|llega ma[nñ]ana|regi[oó]n metropolitana)/i,
      ],
      [
        'pago/cuotas',
        /(cuota|cuotas|medio.? de pago|formas? de pago|mercado ?pago|tarjeta|d[eé]bito|cr[eé]dito|transferencia|pagar)/i,
      ],
      [
        'stock/disponibilidad',
        /(stock|disponib|agotad|quedan|unidades|preventa|pre-venta|reposici[oó]n)/i,
      ],
      [
        'reseñas/confianza',
        /(rese[nñ]a|opini[oó]n|review|valoraci[oó]n|estrella|testimonio|comentarios? de clientes)/i,
      ],
      [
        'cambios/garantía',
        /(cambio|devoluci[oó]n|garant[ií]a|reembolso|derecho a retracto)/i,
      ],
      [
        'seguridad/uso',
        /(seguridad|edad recomendada|\bedad\b|peso m[aá]ximo|peso maximo|\bmaterial\b|instalaci[oó]n|instrucci[oó]n|\buso\b|certific)/i,
      ],
    ];

    for (const [
      topic,
      pattern,
    ] of rules) {
      if (pattern.test(text)) {
        return topic;
      }
    }

    return '';
  }

  function trackInformationalInterest(
    target,
    clickType
  ) {
    if (!(target instanceof Element)) {
      return;
    }

    if (
      [
        'checkout_click',
        'add_to_cart_click',
      ].includes(clickType)
    ) {
      return;
    }

    const topic =
      detectInfoTopic(target);

    if (!topic) return;

    trackEvent(
      'info_interest',
      {
        topic,

        label: safeString(
          target.getAttribute?.(
            'aria-label'
          ) ||
            target.getAttribute?.(
              'title'
            ) ||
            target.textContent,
          80
        ),

        product:
          currentProductName(),

        selector:
          buildSelector(target),
      },
      {
        cart: false,
        clarity: false,
      }
    );
  }

  function listenForProductOptions() {
    document.addEventListener(
      'change',
      (event) => {
        const target =
          event.target instanceof Element
            ? event.target
            : null;

        if (
          !target ||
          pageCategory() !==
            'product'
        ) {
          return;
        }

        if (
          !target.matches(
            'select, input[type="radio"], input[type="checkbox"]'
          )
        ) {
          return;
        }

        const productScope =
          target.closest(
            'form[action*="/cart/add"], variant-selects, variant-radios, [class*="variant"], [data-product-form]'
          );

        if (!productScope) return;

        const rawName = safeString(
          target.getAttribute('name') ||
            target
              .closest('fieldset')
              ?.querySelector('legend')
              ?.textContent ||
            target
              .closest('label')
              ?.textContent ||
            target.getAttribute(
              'aria-label'
            ) ||
            target.id,
          80
        );

        const structuralText =
          `${rawName} ${
            target.id || ''
          } ${
            target.className || ''
          }`.toLowerCase();

        if (
          !/(option|variant|color|colour|size|tama[nñ]o|medida|modelo)/i.test(
            structuralText
          ) &&
          !target.closest(
            'variant-selects, variant-radios'
          )
        ) {
          return;
        }

        const value =
          target.matches(
            'input[type="checkbox"], input[type="radio"]'
          ) && !target.checked
            ? ''
            : safeString(
                target.value ||
                  target.getAttribute(
                    'data-value'
                  ) ||
                  '',
                80
              );

        if (!value) return;

        trackEvent(
          'variant_interaction',
          {
            option_name:
              rawName || 'Variante',
            option_value: value,
            product:
              currentProductName(),
          },
          {
            cart: false,
            clarity: false,
          }
        );

        window.setTimeout(
          () =>
            inspectProductAvailability(
              'variant_change'
            ),
          120
        );
      },
      true
    );
  }

  function listenForCommerceSignals() {
    const onceKeys = new Set();

    document.addEventListener(
      'focusin',
      (event) => {
        const target =
          event.target instanceof Element
            ? event.target
            : null;

        if (
          !target ||
          !target.matches(
            'input, select, textarea'
          )
        ) {
          return;
        }

        const descriptor =
          safeString(
            [
              target.getAttribute(
                'name'
              ),
              target.getAttribute('id'),
              target.getAttribute(
                'placeholder'
              ),
              target.getAttribute(
                'aria-label'
              ),
            ]
              .filter(Boolean)
              .join(' '),
            180
          ).toLowerCase();

        if (
          /(discount|descuento|promo|coupon|cup[oó]n)/i.test(
            descriptor
          )
        ) {
          const key =
            `discount:${currentPathWithQuery()}`;

          if (onceKeys.has(key)) {
            return;
          }

          onceKeys.add(key);

          trackEvent(
            'discount_field_focus',
            {
              context:
                pageCategory(),
            },
            {
              cart: false,
              clarity: false,
            }
          );
        }
      },
      true
    );

    document.addEventListener(
      'invalid',
      (event) => {
        const target =
          event.target instanceof Element
            ? event.target
            : null;

        if (!target) return;

        const form =
          target.closest(
            'form[action*="/cart"], form[action*="/search"], [data-product-form]'
          );

        if (!form) return;

        const key =
          `invalid:${currentPathWithQuery()}:${safeString(
            target.getAttribute(
              'name'
            ) ||
              target.type ||
              target.tagName,
            40
          )}`;

        if (onceKeys.has(key)) {
          return;
        }

        onceKeys.add(key);

        trackEvent(
          'form_validation_issue',
          {
            field_type: safeString(
              target.getAttribute(
                'type'
              ) ||
                target.tagName,
              30
            ),

            field_name: safeString(
              target.getAttribute(
                'name'
              ) || '',
              50
            ),
          },
          {
            cart: false,
            dataLayer: false,
            clarity: false,
          }
        );
      },
      true
    );
  }

  function inspectProductAvailability(
    trigger = 'load'
  ) {
    if (
      pageCategory() !== 'product'
    ) {
      return;
    }

    const button =
      document.querySelector(
        '[name="add"], form[action*="/cart/add"] button[type="submit"], [data-type="add-to-cart-form"] button[type="submit"]'
      );

    if (!(button instanceof Element)) {
      return;
    }

    const label = safeString(
      button.getAttribute(
        'aria-label'
      ) || button.textContent,
      100
    ).toLowerCase();

    const unavailable =
      Boolean(
        button.disabled ||
          button.getAttribute(
            'aria-disabled'
          ) === 'true'
      ) &&
      /(agotad|sold out|unavailable|no disponible|sin stock)/i.test(
        label
      );

    if (!unavailable) return;

    const variant = safeString(
      document.querySelector(
        'form[action*="/cart/add"] [name="id"]'
      )?.value ||
        CONFIG.page?.product
          ?.variantId ||
        '',
      60
    );

    const signature =
      `${currentPathWithQuery()}|${variant}|${label}`;

    currentSession._availability_signatures =
      Array.isArray(
        currentSession._availability_signatures
      )
        ? currentSession._availability_signatures
        : [];

    if (
      currentSession._availability_signatures.includes(
        signature
      )
    ) {
      return;
    }

    currentSession._availability_signatures.push(
      signature
    );

    currentSession._availability_signatures =
      currentSession._availability_signatures.slice(
        -8
      );

    trackEvent(
      'unavailable_variant',
      {
        product:
          currentProductName(),
        variant_id: variant,
        trigger,
      },
      {
        cart: false,
        clarity: false,
      }
    );
  }

  function detectPurchasePage() {
    const path =
      window.location.pathname.toLowerCase();

    const title = safeString(
      document.title,
      120
    ).toLowerCase();

    const looksConfirmed =
      /thank[_-]?you|order-confirm|orders?\/.*\/thank/.test(
        path
      ) ||
      /(gracias por tu compra|pedido confirmado|order confirmed|thank you for your purchase)/i.test(
        title
      );

    if (
      looksConfirmed &&
      !eventExists(
        'purchase_detected'
      )
    ) {
      trackEvent(
        'purchase_detected',
        {
          path:
            currentPathWithQuery(),
        },
        {
          cart: false,
          clarity: false,
        }
      );
    }
  }

  function pendingClicks() {
    return safeParse(
      sessionStorage.getItem(
        PENDING_CLICKS_KEY
      ),
      []
    );
  }

  function storePendingClick(click) {
    const items = pendingClicks();

    items.push(click);

    try {
      sessionStorage.setItem(
        PENDING_CLICKS_KEY,
        JSON.stringify(
          items.slice(-30)
        )
      );
    } catch (_) {
      // noop
    }
  }

  function clearPendingClicks() {
    try {
      sessionStorage.removeItem(
        PENDING_CLICKS_KEY
      );
    } catch (_) {
      // noop
    }
  }

  function detectRageClick(click) {
    const current = nowMs();

    rageClicks.push({
      time: current,
      x: click.client_x,
      y: click.client_y,
    });

    rageClicks = rageClicks.filter(
      (item) =>
        current - item.time <= 1500
    );

    const nearby =
      rageClicks.filter(
        (item) =>
          Math.abs(
            item.x - click.client_x
          ) < 35 &&
          Math.abs(
            item.y - click.client_y
          ) < 35
      );

    if (
      nearby.length >= 3 &&
      !eventExists('rage_click')
    ) {
      trackEvent(
        'rage_click',
        {
          selector: click.selector,
          click_type:
            click.click_type,
        },
        {
          cart: false,
        }
      );
    }
  }

  function recordClick(event) {
    const rawTarget =
      event.target instanceof Element
        ? event.target
        : document.body;

    const target =
      rawTarget.closest?.(
        'a, button, input, select, textarea, summary, label, [role="button"], [onclick]'
      ) || rawTarget;

    const docHeight =
      Math.max(
        document.documentElement
          .scrollHeight,
        document.body.scrollHeight,
        1
      );

    const clickType =
      classifyClick(target);

    const click = cleanObject({
      id: generateId('click'),
      timestamp: nowIso(),
      session_id:
        currentSession.id,
      path: currentPathWithQuery(),
      page_type: pageCategory(),
      click_type: clickType,
      selector:
        buildSelector(target),
      tag:
        target.tagName?.toLowerCase() ||
        '',
      label: safeString(
        target.getAttribute?.(
          'aria-label'
        ) ||
          target.getAttribute?.(
            'title'
          ) ||
          target.textContent,
        100
      ),
      href: safeHref(target),
      client_x: Math.round(
        event.clientX || 0
      ),
      client_y: Math.round(
        event.clientY || 0
      ),
      page_x: Math.round(
        event.pageX || 0
      ),
      page_y: Math.round(
        event.pageY || 0
      ),
      x_percent: Number(
        (
          ((event.clientX || 0) /
            Math.max(
              window.innerWidth,
              1
            )) *
          100
        ).toFixed(2)
      ),
      y_document_percent:
        Number(
          (
            ((event.pageY || 0) /
              docHeight) *
            100
          ).toFixed(2)
        ),
      viewport_width:
        window.innerWidth || 0,
      viewport_height:
        window.innerHeight || 0,
      scroll_y: Math.round(
        window.scrollY || 0
      ),
    });

    currentSession.click_count += 1;

    touchSession();
    saveState();
    detectRageClick(click);

    trackInformationalInterest(
      target,
      clickType
    );

    if (
      currentSession.clicks_saved <
      MAX_FIREBASE_CLICKS
    ) {
      currentSession.clicks_saved += 1;
      storePendingClick(click);
      flushPendingClicksSoon();
    }

    if (
      [
        'checkout_click',
        'whatsapp_click',
        'showroom_click',
        'discount_interaction',
      ].includes(clickType)
    ) {
      trackEvent(
        clickType,
        {
          label: click.label,
          href: click.href,
          selector: click.selector,
        },
        {
          cart: true,
        }
      );
    }

    if (
      clickType ===
        'add_to_cart_click' &&
      !target.closest(
        'form[action*="/cart/add"]'
      )
    ) {
      trackEvent(
        'add_to_cart_intent',
        {
          product:
            currentProductName(),
          selector: click.selector,
        },
        {
          cart: false,
        }
      );
    }

    if (
      clickType ===
      'checkout_click'
    ) {
      const checkoutPath =
        currentPathWithQuery();

      syncCartAttributes(true);

      window.setTimeout(() => {
        if (
          document.visibilityState !==
          'visible'
        ) {
          return;
        }

        if (
          currentPathWithQuery() !==
          checkoutPath
        ) {
          return;
        }

        if (
          eventExists(
            'checkout_stalled'
          )
        ) {
          return;
        }

        trackEvent(
          'checkout_stalled',
          {
            label: click.label,
            href: click.href,
          },
          {
            cart: false,
          }
        );
      }, 3500);
    }

    if (
      clickType ===
      'search_submit'
    ) {
      const form =
        target.closest('form');

      const term =
        safeSearchTerm(
          form?.querySelector(
            'input[type="search"]'
          )?.value
        );

      if (term) {
        trackEvent(
          'search_submit',
          {
            term,
          },
          {
            cart: false,
          }
        );
      }
    }
  }

  function preferredTouch() {
    return (
      state.first_marketing_touch ||
      state.first_touch ||
      state.last_touch || {
        source: 'direct',
      }
    );
  }

  function formatMoneyCents(cents) {
    const currency =
      CONFIG.shop?.currency ||
      'CLP';

    try {
      return new Intl.NumberFormat(
        'es-CL',
        {
          style: 'currency',
          currency,
          maximumFractionDigits:
            currency === 'CLP'
              ? 0
              : 2,
        }
      ).format(
        (Number(cents) || 0) /
          100
      );
    } catch (_) {
      return String(cents || 0);
    }
  }

  function normalizeCart(cart) {
    return {
      item_count: Number(
        cart?.item_count || 0
      ),

      total_price: Number(
        cart?.total_price || 0
      ),

      items: (
        cart?.items || []
      ).map((item) => ({
        key: safeString(
          item.key || item.id,
          160
        ),
        variant_id:
          item.variant_id ||
          item.id ||
          null,
        product_id:
          item.product_id || null,
        handle: safeString(
          item.handle,
          120
        ),
        title: safeString(
          item.product_title ||
            item.title,
          180
        ),
        variant_title:
          safeString(
            item.variant_title,
            120
          ),
        quantity: Number(
          item.quantity || 0
        ),
        final_price: Number(
          item.final_price ||
            item.price ||
            0
        ),
        final_line_price:
          Number(
            item.final_line_price ||
              0
          ),
      })),
    };
  }

  function diffCart(
    previous,
    next
  ) {
    if (!previous) return;

    const oldItems = new Map(
      previous.items.map((item) => [
        item.key ||
          String(item.variant_id),
        item,
      ])
    );

    const newItems = new Map(
      next.items.map((item) => [
        item.key ||
          String(item.variant_id),
        item,
      ])
    );

    newItems.forEach(
      (item, key) => {
        const old =
          oldItems.get(key);

        if (!old) {
          trackEvent(
            'add_to_cart',
            {
              product_id:
                item.product_id,
              variant_id:
                item.variant_id,
              title: item.title,
              variant_title:
                item.variant_title,
              quantity:
                item.quantity,
              price:
                item.final_price,
              price_formatted:
                formatMoneyCents(
                  item.final_price
                ),
            }
          );
        } else if (
          item.quantity !==
          old.quantity
        ) {
          const difference =
            item.quantity -
            old.quantity;

          trackEvent(
            'quantity_change',
            {
              product_id:
                item.product_id,
              variant_id:
                item.variant_id,
              title: item.title,
              previous_quantity:
                old.quantity,
              new_quantity:
                item.quantity,
              difference,
            }
          );
        }
      }
    );

    oldItems.forEach(
      (item, key) => {
        if (
          !newItems.has(key)
        ) {
          const removedValue =
            Number(
              item.final_line_price ||
                (item.final_price ||
                  0) *
                  (item.quantity ||
                    0)
            );

          trackEvent(
            'remove_from_cart',
            {
              product_id:
                item.product_id,
              variant_id:
                item.variant_id,
              title: item.title,
              removed_quantity:
                item.quantity,
              removed_value:
                removedValue,
              removed_value_formatted:
                formatMoneyCents(
                  removedValue
                ),
            }
          );

          currentSession.removed_cart_value =
            Number(
              currentSession.removed_cart_value ||
                0
            ) + removedValue;
        }
      }
    );
  }

  async function readCart() {
    if (!originalFetch) {
      return null;
    }

    try {
      const response =
        await originalFetch(
          `${
            window.Shopify?.routes
              ?.root || '/'
          }cart.js`,
          {
            headers: {
              Accept:
                'application/json',
            },
            cache: 'no-store',
            credentials:
              'same-origin',
          }
        );

      if (!response.ok) {
        return null;
      }

      return normalizeCart(
        await response.json()
      );
    } catch (error) {
      log(
        'No se pudo leer el carrito:',
        error
      );

      return null;
    }
  }

  async function refreshCartAndTrack() {
    const next =
      await readCart();

    if (!next) return;

    diffCart(
      cartSnapshot,
      next
    );

    cartSnapshot = next;
    currentSession.cart = next;

    currentSession.peak_cart_total =
      Math.max(
        Number(
          currentSession.peak_cart_total ||
            0
        ),
        Number(next.total_price || 0)
      );

    currentSession.peak_cart_items =
      Math.max(
        Number(
          currentSession.peak_cart_items ||
            0
        ),
        Number(next.item_count || 0)
      );

    touchSession();
    recalculateIntent();
    saveState();
    scheduleFirebaseSync();
    scheduleCartSync();
  }

  function requestUrl(input) {
    if (
      typeof input === 'string'
    ) {
      return input;
    }

    if (input instanceof URL) {
      return input.href;
    }

    return input?.url || '';
  }

  function isCartMutation(
    url,
    init
  ) {
    if (internalCartUpdate) {
      return false;
    }

    const method = String(
      init?.method || 'GET'
    ).toUpperCase();

    if (
      ![
        'POST',
        'PUT',
        'PATCH',
      ].includes(method)
    ) {
      return false;
    }

    return /\/cart\/(add|change|update)(\.js)?(?:\?|$)/.test(
      url
    );
  }

  function installFetchMonitor() {
    if (
      !originalFetch ||
      window.__BE_JOURNEY_FETCH_MONITOR__
    ) {
      return;
    }

    window.__BE_JOURNEY_FETCH_MONITOR__ =
      true;

    window.fetch =
      async function beJourneyFetch(
        input,
        init = {}
      ) {
        const url =
          requestUrl(input);

        const monitor =
          isCartMutation(
            url,
            init
          );

        try {
          const response =
            await originalFetch(
              input,
              init
            );

          if (monitor) {
            if (!response.ok) {
              const action =
                /\/cart\/add/.test(
                  url
                )
                  ? 'add'
                  : /\/cart\/change/.test(
                        url
                      )
                    ? 'change'
                    : 'update';

              trackEvent(
                'cart_request_error',
                {
                  action,
                  status:
                    response.status,
                },
                {
                  cart: false,
                }
              );
            }

            window.setTimeout(
              refreshCartAndTrack,
              120
            );

            window.setTimeout(
              refreshCartAndTrack,
              600
            );
          }

          return response;
        } catch (error) {
          if (monitor) {
            const action =
              /\/cart\/add/.test(url)
                ? 'add'
                : /\/cart\/change/.test(
                      url
                    )
                  ? 'change'
                  : 'update';

            trackEvent(
              'cart_request_error',
              {
                action,
                status: 0,
                message:
                  safeString(
                    error?.message ||
                      'Error de red',
                    100
                  ),
              },
              {
                cart: false,
              }
            );
          }

          throw error;
        }
      };
  }

  function listenForNativeCartForms() {
    document.addEventListener(
      'submit',
      (event) => {
        const form =
          event.target instanceof
          HTMLFormElement
            ? event.target
            : null;

        if (!form) return;

        if (
          form.matches(
            'form[action*="/cart/add"]'
          )
        ) {
          const variantId =
            safeString(
              form.querySelector(
                '[name="id"]'
              )?.value,
              50
            );

          const requestedQuantity =
            Number(
              form.querySelector(
                '[name="quantity"]'
              )?.value || 1
            );

          const beforeQuantity =
            cartVariantQuantity(
              cartSnapshot ||
                currentSession.cart,
              variantId
            );

          trackEvent(
            'add_to_cart_intent',
            {
              variant_id:
                variantId,
              quantity:
                requestedQuantity,
            },
            {
              cart: false,
            }
          );

          window.setTimeout(
            refreshCartAndTrack,
            800
          );

          window.setTimeout(
            async () => {
              const next =
                await readCart();

              if (
                !next ||
                !variantId
              ) {
                return;
              }

              const afterQuantity =
                cartVariantQuantity(
                  next,
                  variantId
                );

              if (
                afterQuantity <=
                beforeQuantity
              ) {
                trackEvent(
                  'add_to_cart_failed',
                  {
                    variant_id:
                      variantId,
                    requested_quantity:
                      requestedQuantity,
                    before_quantity:
                      beforeQuantity,
                    after_quantity:
                      afterQuantity,
                  },
                  {
                    cart: false,
                  }
                );
              }
            },
            1800
          );
        }

        if (
          form.matches(
            'form[action*="/search"]'
          )
        ) {
          const term =
            safeSearchTerm(
              form.querySelector(
                'input[type="search"]'
              )?.value
            );

          if (term) {
            trackEvent(
              'search_submit',
              {
                term,
              },
              {
                cart: false,
              }
            );
          }
        }
      },
      true
    );
  }

  function cleanPageTitle(title) {
    return safeString(
      title || '',
      120
    )
      .replace(
        /\|\s*Baby Elephant.*$/i,
        ''
      )
      .replace(
        /[-–—]\s*Baby Elephant.*$/i,
        ''
      )
      .trim();
  }

  function sourceName(source) {
    const names = {
      meta: 'Meta Ads',
      facebook: 'Facebook',
      instagram: 'Instagram',
      google: 'Google Ads',
      'google-organic':
        'Google orgánico',
      tiktok: 'TikTok Ads',
      'tiktok-organic':
        'TikTok orgánico',
      bing: 'Bing Ads',
      'bing-organic':
        'Bing orgánico',
      referral:
        'Referencia externa',
      direct: 'Tráfico directo',
    };

    return (
      names[source] ||
      safeString(
        source || 'Directo',
        80
      )
    );
  }

  function formatDuration(seconds) {
    const total = Math.max(
      0,
      Number(seconds || 0)
    );

    if (total < 60) {
      return `${total} seg`;
    }

    const minutes =
      Math.round(total / 60);

    if (minutes < 60) {
      return `${minutes} min`;
    }

    const hours =
      Math.floor(minutes / 60);

    const remaining =
      minutes % 60;

    return remaining
      ? `${hours} h ${remaining} min`
      : `${hours} h`;
  }

  function formatDateTime(value) {
    if (!value) return '-';

    try {
      return new Intl.DateTimeFormat(
        'es-CL',
        {
          day: '2-digit',
          month: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        }
      ).format(new Date(value));
    } catch (_) {
      return safeString(
        value,
        30
      );
    }
  }

  function countEvent(
    type,
    session = currentSession
  ) {
    return (
      session.events || []
    ).filter(
      (event) =>
        event.type === type
    ).length;
  }

  function eventsOfType(
    type,
    session = currentSession
  ) {
    return (
      session.events || []
    ).filter(
      (event) =>
        event.type === type
    );
  }

  function cartVariantQuantity(
    cart,
    variantId
  ) {
    if (!variantId) return 0;

    return (
      cart?.items || []
    ).reduce((sum, item) => {
      return String(
        item.variant_id
      ) === String(variantId)
        ? sum +
            Number(
              item.quantity || 0
            )
        : sum;
    }, 0);
  }

  function pageLabelForPath(path) {
    if (!path) {
      return 'página no identificada';
    }

    const page = [
      ...(currentSession.important_pages ||
        []),
    ]
      .reverse()
      .find(
        (item) =>
          item.path === path
      );

    return page
      ? pageName(page)
      : safeString(path, 90);
  }

  function eventLocation(event) {
    const details =
      event?.details || {};

    const product =
      cleanPageTitle(
        details.title ||
          details.product?.title ||
          details.product_title ||
          ''
      );

    const base =
      product ||
      pageLabelForPath(
        event?.path
      );

    return safeString(
      base,
      110
    );
  }

  function pageName(page) {
    const title =
      cleanPageTitle(
        page?.title
      );

    const type = safeString(
      page?.type,
      40
    ).toLowerCase();

    if (type === 'index') {
      return 'Inicio';
    }

    if (type === 'product') {
      return title
        ? `Producto: ${title}`
        : 'Producto';
    }

    if (
      type === 'collection'
    ) {
      return title
        ? `Colección: ${title}`
        : 'Colección';
    }

    if (type === 'search') {
      return 'Búsqueda';
    }

    if (type === 'cart') {
      return 'Carrito';
    }

    if (type === 'checkout') {
      return 'Checkout';
    }

    return title || 'Página';
  }

  function productSummary() {
    const products =
      Object.values(
        currentSession.products ||
          {}
      )
        .sort((a, b) => {
          const scoreA =
            (a.views || 0) * 20 +
            (a.active_seconds || 0);

          const scoreB =
            (b.views || 0) * 20 +
            (b.active_seconds || 0);

          return scoreB - scoreA;
        })
        .slice(0, 6);

    if (!products.length) {
      return 'Sin productos revisados';
    }

    return products
      .map((product) => {
        const title =
          cleanPageTitle(
            product.title ||
              product.handle
          );

        const views = Number(
          product.views || 0
        );

        const time =
          formatDuration(
            product.active_seconds ||
              0
          );

        return `${title} · ${views} vista${
          views === 1 ? '' : 's'
        } · ${time}`;
      })
      .join(' | ')
      .slice(0, 240);
  }

  function cartItemsSummary() {
    const cart =
      currentSession.cart || {};

    const items = (
      cart.items || []
    ).slice(0, 5);

    if (!items.length) {
      return `🛍️ ${Number(
        cart.item_count || 0
      )} productos · ${formatMoneyCents(
        cart.total_price || 0
      )}`;
    }

    const productText =
      items
        .map(
          (item) =>
            `${cleanPageTitle(
              item.title
            )} x${item.quantity}`
        )
        .join(' | ');

    return `🛍️ ${Number(
      cart.item_count || 0
    )} productos · ${formatMoneyCents(
      cart.total_price || 0
    )} · ${productText}`.slice(
      0,
      240
    );
  }

  function searchSummary() {
    const searches = [];

    state.sessions.forEach(
      (session) => {
        (
          session.searches || []
        ).forEach((item) => {
          const term =
            safeString(
              item.term,
              70
            );

          if (
            term &&
            !searches.includes(
              term
            )
          ) {
            searches.push(term);
          }
        });
      }
    );

    return searches.length
      ? searches
          .slice(-8)
          .map(
            (term) =>
              `“${term}”`
          )
          .join(' · ')
          .slice(0, 240)
      : 'Sin búsquedas registradas';
  }

  function actionSummary() {
    const counts = {
      add: countEvent(
        'add_to_cart'
      ),
      remove: countEvent(
        'remove_from_cart'
      ),
      quantity: countEvent(
        'quantity_change'
      ),
      cart: countEvent(
        'cart_view'
      ),
      checkout: countEvent(
        'checkout_click'
      ),
      whatsapp: countEvent(
        'whatsapp_click'
      ),
      showroom: countEvent(
        'showroom_click'
      ),
      discount:
        countEvent(
          'discount_interaction'
        ) +
        countEvent(
          'discount_field_focus'
        ),
      purchase: countEvent(
        'purchase_detected'
      ),
      search:
        countEvent(
          'search_submit'
        ) +
        countEvent(
          'search_view'
        ),
    };

    const actions = [];

    if (counts.search) {
      actions.push(
        `🔎 ${counts.search} búsqueda${
          counts.search === 1
            ? ''
            : 's'
        }`
      );
    }

    if (counts.add) {
      actions.push(
        `➕ ${counts.add} agregado${
          counts.add === 1
            ? ''
            : 's'
        }`
      );
    }

    if (counts.quantity) {
      actions.push(
        `🔢 ${counts.quantity} cambio${
          counts.quantity === 1
            ? ''
            : 's'
        } de cantidad`
      );
    }

    if (counts.remove) {
      actions.push(
        `➖ ${counts.remove} eliminado${
          counts.remove === 1
            ? ''
            : 's'
        }`
      );
    }

    if (counts.cart) {
      actions.push(
        `🛒 ${counts.cart} visita${
          counts.cart === 1
            ? ''
            : 's'
        } al carrito`
      );
    }

    if (counts.whatsapp) {
      actions.push(
        '💬 Abrió WhatsApp'
      );
    }

    if (counts.showroom) {
      actions.push(
        '🏠 Revisó showroom'
      );
    }

    if (counts.discount) {
      actions.push(
        '🏷️ Interactuó con descuento'
      );
    }

    if (counts.checkout) {
      actions.push(
        '💳 Avanzó al checkout'
      );
    }

    if (counts.purchase) {
      actions.push(
        '✅ Compra confirmada detectada'
      );
    }

    return actions.length
      ? actions
          .join(' · ')
          .slice(0, 240)
      : 'Navegó sin acciones comerciales fuertes';
  }

  function behaviorSummary() {
    const pages =
      currentSession.page_count ||
      0;

    const products =
      Object.keys(
        currentSession.products ||
          {}
      ).length;

    const repeatViews =
      Object.values(
        currentSession.products ||
          {}
      ).reduce(
        (sum, product) =>
          sum +
          Math.max(
            0,
            Number(
              product.views || 0
            ) - 1
          ),
        0
      );

    return [
      `📄 ${pages} página${
        pages === 1 ? '' : 's'
      }`,
      `👀 ${products} producto${
        products === 1 ? '' : 's'
      }`,
      `🔁 ${repeatViews} revisita${
        repeatViews === 1
          ? ''
          : 's'
      }`,
      `⏱️ ${formatDuration(
        currentSession.active_seconds
      )}`,
      `📜 ${
        currentSession.max_scroll_percent ||
        0
      }% scroll`,
      `👆 ${
        currentSession.click_count ||
        0
      } clic${
        currentSession.click_count ===
        1
          ? ''
          : 's'
      }`,
    ]
      .join(' · ')
      .slice(0, 240);
  }

  function topicCounts(
    session = currentSession
  ) {
    const counts = {};

    (
      session?.events || []
    ).forEach((event) => {
      if (
        event.type !==
        'info_interest'
      ) {
        return;
      }

      const topic =
        safeString(
          event.details?.topic,
          50
        );

      if (!topic) return;

      counts[topic] =
        (counts[topic] || 0) + 1;
    });

    return counts;
  }

  function allRecentSessions() {
    return (
      state.sessions || []
    ).slice(-5);
  }

  function countEventAcross(type) {
    return allRecentSessions().reduce(
      (sum, session) =>
        sum +
        countEvent(type, session),
      0
    );
  }

  function mainProductAcrossSessions() {
    const map = new Map();

    allRecentSessions().forEach(
      (session) => {
        Object.values(
          session.products || {}
        ).forEach((product) => {
          const key =
            product.handle ||
            product.id ||
            product.title;

          if (!key) return;

          const existing =
            map.get(key) || {
              title:
                cleanPageTitle(
                  product.title ||
                    product.handle ||
                    ''
                ),
              views: 0,
              active_seconds: 0,
              sessions: 0,
            };

          existing.views +=
            Number(
              product.views || 0
            );

          existing.active_seconds +=
            Number(
              product.active_seconds ||
                0
            );

          existing.sessions += 1;

          map.set(key, existing);
        });
      }
    );

    return (
      [...map.values()].sort(
        (a, b) => {
          const scoreA =
            a.views * 30 +
            a.active_seconds +
            a.sessions * 20;

          const scoreB =
            b.views * 30 +
            b.active_seconds +
            b.sessions * 20;

          return scoreB - scoreA;
        }
      )[0] || null
    );
  }

  function behavioralFrictionSignals() {
    const signals = [];
    const counts = topicCounts();

    const hasCheckout =
      countEvent(
        'checkout_click'
      ) > 0;

    const hasCart =
      Number(
        currentSession.cart
          ?.item_count || 0
      ) > 0 ||
      countEvent(
        'add_to_cart'
      ) > 0;

    const variantChanges =
      countEvent(
        'variant_interaction'
      );

    const adds =
      countEvent('add_to_cart');

    const cartViews =
      countEvent('cart_view');

    const discount =
      countEvent(
        'discount_interaction'
      ) +
      countEvent(
        'discount_field_focus'
      );

    Object.entries(counts)
      .filter(
        ([, count]) =>
          count >= 2
      )
      .sort(
        (a, b) => b[1] - a[1]
      )
      .slice(0, 3)
      .forEach(
        ([topic, count]) => {
          if (!hasCheckout) {
            signals.push(
              `Posible duda en ${topic}: ${count} revisiones`
            );
          }
        }
      );

    if (
      variantChanges >= 3 &&
      adds === 0
    ) {
      signals.push(
        `Posible indecisión de variante/medida: ${variantChanges} cambios sin agregar`
      );
    }

    if (
      discount &&
      !hasCheckout &&
      (
        hasCart ||
        Object.values(
          currentSession.products ||
            {}
        ).some(
          (p) =>
            Number(
              p.views || 0
            ) >= 2
        )
      )
    ) {
      signals.push(
        'Posible sensibilidad a precio/descuento'
      );
    }

    if (
      cartViews >= 2 &&
      !hasCheckout
    ) {
      signals.push(
        'Posible duda final: volvió al carrito sin avanzar'
      );
    }

    return [
      ...new Set(signals),
    ].slice(0, 5);
  }

  function objectionAreasSummary() {
    const counts = topicCounts();

    const labels =
      Object.entries(counts)
        .sort(
          (a, b) =>
            b[1] - a[1]
        )
        .slice(0, 5)
        .map(
          ([topic, count]) => {
            const icon =
              topic.startsWith(
                'medidas'
              )
                ? '📏'
                : topic.startsWith(
                      'despacho'
                    )
                  ? '🚚'
                  : topic.startsWith(
                        'pago'
                      )
                    ? '💳'
                    : topic.startsWith(
                          'stock'
                        )
                      ? '📦'
                      : topic.startsWith(
                            'reseñas'
                          )
                        ? '⭐'
                        : topic.startsWith(
                              'cambios'
                            )
                          ? '↩️'
                          : '🛡️';

            return `${icon} ${topic}: ${count}`;
          }
        );

    const variantChanges =
      countEvent(
        'variant_interaction'
      );

    if (variantChanges) {
      labels.push(
        `🎛️ Variantes/medidas probadas: ${variantChanges}`
      );
    }

    if (
      countEvent(
        'discount_interaction'
      ) +
        countEvent(
          'discount_field_focus'
        )
    ) {
      labels.push(
        '🏷️ Revisó descuento/cupón'
      );
    }

    return labels.length
      ? labels
          .join(' · ')
          .slice(0, 240)
      : 'Sin áreas de duda explícitas detectadas';
  }

  function customerEvolutionSummary() {
    const sessions =
      allRecentSessions();

    const totalActive =
      sessions.reduce(
        (sum, session) =>
          sum +
          Number(
            session.active_seconds ||
              0
          ),
        0
      );

    const scores =
      sessions.map(
        (session) =>
          Number(
            session.score || 0
          )
      );

    const previous =
      scores.length >= 2
        ? scores[
            scores.length - 2
          ]
        : null;

    const current =
      scores[
        scores.length - 1
      ] || 0;

    let evolution =
      'Primera sesión';

    if (previous !== null) {
      const delta =
        current - previous;

      evolution =
        delta >= 8
          ? `Intención sube ${previous}→${current} (+${delta})`
          : delta <= -8
            ? `Intención baja ${previous}→${current} (${delta})`
            : `Intención estable ${previous}→${current}`;
    }

    const main =
      mainProductAcrossSessions();

    return [
      `${sessions.length} sesión${
        sessions.length === 1
          ? ''
          : 'es'
      }`,
      `${formatDuration(
        totalActive
      )} activos acumulados`,
      evolution,
      main?.sessions >= 2
        ? `Volvió a ${safeString(
            main.title,
            55
          )} en ${
            main.sessions
          } sesiones`
        : '',
    ]
      .filter(Boolean)
      .join(' · ')
      .slice(0, 240);
  }

  function evidenceConfidence() {
    let score = 0;

    score += Math.min(
      allRecentSessions().length *
        2,
      6
    );

    score += Math.min(
      Object.keys(
        currentSession.products ||
          {}
      ).length,
      4
    );

    score += Math.min(
      countEvent('info_interest'),
      4
    );

    score += Math.min(
      countEvent(
        'variant_interaction'
      ),
      3
    );

    if (
      countEvent('add_to_cart')
    ) {
      score += 3;
    }

    if (
      countEvent('cart_view')
    ) {
      score += 2;
    }

    if (
      countEvent('checkout_click')
    ) {
      score += 3;
    }

    if (
      countEvent(
        'remove_from_cart'
      )
    ) {
      score += 2;
    }

    if (
      countEvent(
        'purchase_detected'
      )
    ) {
      score += 4;
    }

    if (
      currentSession.stage_history
        ?.length >= 3
    ) {
      score += 2;
    }

    if (
      currentSession.active_seconds >=
      60
    ) {
      score += 2;
    }

    if (score >= 12) {
      return {
        label: 'Alta',
        emoji: '🟢',
        score,
      };
    }

    if (score >= 6) {
      return {
        label: 'Media',
        emoji: '🟡',
        score,
      };
    }

    return {
      label: 'Baja',
      emoji: '⚪',
      score,
    };
  }

  function readingQualitySummary() {
    const confidence =
      evidenceConfidence();

    return `${confidence.emoji} Confianza ${confidence.label} · evidencia ${confidence.score} pts · las objeciones se expresan como “posibles” cuando son inferidas del comportamiento`;
  }

  function journeyStageSummary() {
    const stage =
      deriveStageCode(
        currentSession
      );

    const labels = {
      purchase:
        '✅ Compra confirmada · conversión detectada',
      checkout:
        '💳 Checkout · intención de compra muy alta',
      cart:
        '🛒 Carrito · decisión avanzada, aún no completa',
      evaluation:
        '🤔 Evaluación · compara/valida antes de decidir',
      consideration:
        '👀 Consideración · revisando producto',
      discovery:
        '🔎 Descubrimiento · exploración inicial',
    };

    return (
      labels[stage] ||
      labels.discovery
    );
  }

  function performanceFrictionSignals() {
    const perf =
      currentSession.performance ||
      {};

    const issues = [];

    if (
      Number(
        perf.lcp_ms || 0
      ) > 4000
    ) {
      issues.push(
        `LCP lento ${Math.round(
          perf.lcp_ms
        )}ms`
      );
    }

    if (
      Number(
        perf.inp_ms || 0
      ) > 500
    ) {
      issues.push(
        `INP lento ${Math.round(
          perf.inp_ms
        )}ms`
      );
    }

    if (
      Number(perf.cls || 0) >
      0.25
    ) {
      issues.push(
        `CLS alto ${Number(
          perf.cls
        ).toFixed(2)}`
      );
    }

    if (
      Number(
        perf.ttfb_ms || 0
      ) > 1800
    ) {
      issues.push(
        `TTFB lento ${Math.round(
          perf.ttfb_ms
        )}ms`
      );
    }

    if (
      Number(
        perf.fcp_ms || 0
      ) > 3000
    ) {
      issues.push(
        `FCP lento ${Math.round(
          perf.fcp_ms
        )}ms`
      );
    }

    return issues;
  }

  function frictionSeverity() {
    let score = 0;

    score +=
      countEvent('js_error') * 4;

    score +=
      countEvent(
        'cart_request_error'
      ) * 5;

    score +=
      countEvent(
        'add_to_cart_failed'
      ) * 5;

    score +=
      countEvent(
        'checkout_stalled'
      ) * 5;

    score +=
      countEvent('rage_click') *
      4;

    score +=
      countEvent(
        'remove_from_cart'
      ) * 3;

    score +=
      countEvent(
        'form_validation_issue'
      ) * 3;

    score +=
      countEvent(
        'unavailable_variant'
      ) * 2;

    score += Math.min(
      countEvent(
        'quantity_change'
      ),
      3
    );

    score +=
      countEvent('exit_intent') *
      2;

    score += Math.min(
      behavioralFrictionSignals()
        .length,
      3
    );

    score += Math.min(
      performanceFrictionSignals()
        .length * 2,
      6
    );

    if (score >= 10) {
      return {
        label: 'Alta',
        emoji: '🔴',
        score,
      };
    }

    if (score >= 5) {
      return {
        label: 'Media',
        emoji: '🟠',
        score,
      };
    }

    if (score >= 1) {
      return {
        label: 'Baja',
        emoji: '🟡',
        score,
      };
    }

    return {
      label:
        'Sin fricción fuerte',
      emoji: '🟢',
      score: 0,
    };
  }

  function frictionSummary() {
    const friction = [];

    const severity =
      frictionSeverity();

    const rage =
      countEvent('rage_click');

    const errors =
      countEvent('js_error');

    const cartErrors =
      countEvent(
        'cart_request_error'
      );

    const removals =
      countEvent(
        'remove_from_cart'
      );

    const exits =
      countEvent('exit_intent');

    const addFails =
      countEvent(
        'add_to_cart_failed'
      );

    const checkoutStalls =
      countEvent(
        'checkout_stalled'
      );

    const invalid =
      countEvent(
        'form_validation_issue'
      );

    const unavailable =
      countEvent(
        'unavailable_variant'
      );

    if (cartErrors) {
      friction.push(
        `🚨 ${cartErrors} error${
          cartErrors === 1
            ? ''
            : 'es'
        } de carrito`
      );
    }

    if (addFails) {
      friction.push(
        `🛑 ${addFails} agregado${
          addFails === 1
            ? ''
            : 's'
        } no confirmado${
          addFails === 1
            ? ''
            : 's'
        }`
      );
    }

    if (checkoutStalls) {
      friction.push(
        `💳 ${checkoutStalls} checkout${
          checkoutStalls === 1
            ? ''
            : 's'
        } estancado${
          checkoutStalls === 1
            ? ''
            : 's'
        }`
      );
    }

    if (rage) {
      friction.push(
        `😤 ${rage} episodio${
          rage === 1 ? '' : 's'
        } de clics repetitivos`
      );
    }

    if (errors) {
      friction.push(
        `⚠️ ${errors} error${
          errors === 1
            ? ''
            : 'es'
        } JS`
      );
    }

    if (invalid) {
      friction.push(
        `🧾 ${invalid} validación${
          invalid === 1
            ? ''
            : 'es'
        } de formulario`
      );
    }

    if (unavailable) {
      friction.push(
        `📦 ${unavailable} variante${
          unavailable === 1
            ? ''
            : 's'
        } no disponible${
          unavailable === 1
            ? ''
            : 's'
        }`
      );
    }

    if (removals) {
      friction.push(
        `➖ ${removals} retiro${
          removals === 1
            ? ''
            : 's'
        } del carrito`
      );
    }

    if (exits) {
      friction.push(
        '🚪 Señal de salida'
      );
    }

    performanceFrictionSignals()
      .slice(0, 2)
      .forEach((item) =>
        friction.push(
          `🐢 ${item}`
        )
      );

    behavioralFrictionSignals()
      .slice(0, 2)
      .forEach((item) =>
        friction.push(
          `🧠 ${item}`
        )
      );

    return friction.length
      ? `${severity.emoji} Fricción ${severity.label} · ${friction.join(
          ' · '
        )}`.slice(
          0,
          ATTRIBUTE_VALUE_MAX
        )
      : '🟢 Sin fricciones fuertes detectadas';
  }

  function frictionDetailText() {
    const interesting = new Set([
      'cart_request_error',
      'add_to_cart_failed',
      'checkout_stalled',
      'rage_click',
      'js_error',
      'remove_from_cart',
      'quantity_change',
      'exit_intent',
      'form_validation_issue',
      'unavailable_variant',
    ]);

    const inferred =
      behavioralFrictionSignals().map(
        (item) =>
          `Inferencia: ${item}`
      );

    const perf =
      performanceFrictionSignals().map(
        (item) =>
          `Rendimiento: ${item}`
      );

    const items = (
      currentSession.events || []
    )
      .filter((event) =>
        interesting.has(event.type)
      )
      .slice(-14)
      .map((event) => {
        const time =
          formatDateTime(
            event.timestamp
          );

        const where =
          eventLocation(event);

        const details =
          event.details || {};

        const labels = {
          cart_request_error:
            `Error de carrito (${safeString(
              details.action ||
                'operación',
              20
            )} · HTTP ${
              details.status ||
              'red'
            }) en ${where}`,

          add_to_cart_failed:
            `No se confirmó el agregado al carrito en ${where}`,

          checkout_stalled:
            `Intentó checkout pero siguió en carrito (${where})`,

          rage_click:
            `Clics repetitivos en ${where}`,

          js_error:
            `Error técnico en ${where}: ${safeString(
              details.message,
              70
            )}`,

          remove_from_cart:
            `Eliminó ${
              cleanPageTitle(
                details.title ||
                  ''
              ) || 'un producto'
            } del carrito${
              details.removed_value_formatted
                ? ` (${details.removed_value_formatted})`
                : ''
            }`,

          quantity_change:
            `Cambió cantidad de ${
              cleanPageTitle(
                details.title ||
                  ''
              ) || 'un producto'
            } (${
              details.previous_quantity
            }→${
              details.new_quantity
            })`,

          exit_intent:
            `Señal de salida desde ${where}`,

          form_validation_issue:
            `Validación de formulario en ${where} (${safeString(
              details.field_name ||
                details.field_type ||
                'campo',
              35
            )})`,

          unavailable_variant:
            `Encontró una variante no disponible en ${where}`,
        };

        return `${time} ${
          labels[event.type] ||
          event.type
        }`;
      });

    const combined = [
      ...inferred,
      ...perf,
      ...items,
    ];

    return combined.length
      ? combined.join(' → ')
      : 'Sin detalle adicional de fricciones';
  }

  function decisionSignalsSummary() {
    const signals = [];

    const products =
      Object.values(
        currentSession.products ||
          {}
      );

    const repeatViews =
      products.reduce(
        (sum, product) =>
          sum +
          Math.max(
            0,
            Number(
              product.views || 0
            ) - 1
          ),
        0
      );

    const cartViews =
      countEvent('cart_view');

    const checkout =
      countEvent(
        'checkout_click'
      );

    const discount =
      countEvent(
        'discount_interaction'
      ) +
      countEvent(
        'discount_field_focus'
      );

    const whatsapp =
      countEvent(
        'whatsapp_click'
      );

    const showroom =
      countEvent(
        'showroom_click'
      );

    const removals =
      countEvent(
        'remove_from_cart'
      );

    const topics =
      topicCounts();

    const variantChanges =
      countEvent(
        'variant_interaction'
      );

    if (products.length >= 3) {
      signals.push(
        `Compara ${products.length} productos`
      );
    }

    if (repeatViews >= 2) {
      signals.push(
        `${repeatViews} revisitas sugieren validación antes de decidir`
      );
    }

    if (discount) {
      signals.push(
        'Posible sensibilidad a precio/descuento'
      );
    }

    if (
      cartViews >= 2 &&
      !checkout
    ) {
      signals.push(
        'Volvió al carrito sin avanzar: posible duda final'
      );
    }

    if (removals) {
      signals.push(
        'Reconsideró al menos un producto en carrito'
      );
    }

    if (whatsapp) {
      signals.push(
        'Buscó apoyo por WhatsApp'
      );
    }

    if (showroom) {
      signals.push(
        'Buscó validación presencial/showroom'
      );
    }

    if (variantChanges >= 2) {
      signals.push(
        `Probó ${variantChanges} variantes/medidas`
      );
    }

    const topTopic =
      Object.entries(topics).sort(
        (a, b) => b[1] - a[1]
      )[0];

    if (topTopic) {
      signals.push(
        `Mayor atención informativa: ${topTopic[0]} (${topTopic[1]})`
      );
    }

    return signals.length
      ? signals
          .map(
            (item) =>
              `• ${item}`
          )
          .join(' · ')
          .slice(0, 240)
      : 'Sin señales claras de objeción o duda';
  }

  function stoppingPointSummary() {
    const pages =
      currentSession.important_pages ||
      [];

    const lastPage =
      pages.length
        ? pageName(
            pages[
              pages.length - 1
            ]
          )
        : pageName({
            type: pageCategory(),
            title:
              document.title,
          });

    const relevant = new Set([
      'add_to_cart',
      'remove_from_cart',
      'quantity_change',
      'cart_view',
      'checkout_click',
      'checkout_stalled',
      'whatsapp_click',
      'showroom_click',
      'discount_interaction',
      'info_interest',
      'variant_interaction',
      'add_to_cart_failed',
      'cart_request_error',
    ]);

    const lastEvent = [
      ...(currentSession.events ||
        []),
    ]
      .reverse()
      .find((event) =>
        relevant.has(event.type)
      );

    const eventText =
      lastEvent
        ? eventDetail(
            lastEvent
          ).replace(
            /^\d{2}\/\d{2},?\s*\d{2}:\d{2}\s*/u,
            ''
          )
        : '';

    const cartCount =
      Number(
        currentSession.cart
          ?.item_count || 0
      );

    return [
      `Terminó en ${lastPage}`,
      eventText
        ? `Última señal: ${eventText}`
        : '',
      cartCount
        ? `Carrito: ${cartCount} producto${
            cartCount === 1
              ? ''
              : 's'
          }`
        : 'Carrito final vacío',
    ]
      .filter(Boolean)
      .join(' · ')
      .slice(0, 240);
  }

  function probableBlockerSummary() {
    const topics =
      topicCounts();

    const hasPurchase =
      countEvent(
        'purchase_detected'
      ) > 0;

    const hasCheckout =
      countEvent(
        'checkout_click'
      ) > 0;

    const cartViews =
      countEvent('cart_view');

    const variantChanges =
      countEvent(
        'variant_interaction'
      );

    const adds =
      countEvent('add_to_cart');

    if (hasPurchase) {
      return '✅ Sin bloqueo final: se detectó confirmación de compra. Las fricciones previas deben leerse como obstáculos superados, no como causa de abandono.';
    }

    if (hasCheckout) {
      const technical =
        countEvent(
          'cart_request_error'
        ) +
        countEvent(
          'add_to_cart_failed'
        ) +
        countEvent(
          'checkout_stalled'
        ) +
        countEvent(
          'js_error'
        );

      if (technical) {
        return '🟢 Fricción superada · Hubo señales técnicas previas, pero el cliente igualmente alcanzó checkout; no atribuir abandono a esas señales.';
      }

      return '✅ Sin bloqueo previo dominante: alcanzó checkout. Este script de storefront no confirma por sí solo el resultado final del pago.';
    }

    if (
      countEvent(
        'cart_request_error'
      )
    ) {
      return '🔴 Hipótesis alta · Problema técnico de carrito. Evidencia: error HTTP/red durante una operación de carrito.';
    }

    if (
      countEvent(
        'add_to_cart_failed'
      )
    ) {
      return '🔴 Hipótesis alta · Problema al agregar. Evidencia: intentó agregar y el cambio de carrito no se confirmó.';
    }

    if (
      countEvent(
        'checkout_stalled'
      )
    ) {
      return '🔴 Hipótesis alta · Bloqueo al avanzar a checkout. Evidencia: clic en checkout sin abandonar el carrito.';
    }

    if (
      countEvent(
        'form_validation_issue'
      )
    ) {
      return '🟠 Hipótesis media · Validación/formulario interrumpió el flujo. Evidencia: el navegador rechazó al menos un campo del formulario.';
    }

    if (
      countEvent(
        'remove_from_cart'
      )
    ) {
      return '🟠 Hipótesis media · Reconsideración de compra. Evidencia: eliminó producto del carrito antes de avanzar.';
    }

    const ranked =
      Object.entries(topics).sort(
        (a, b) => b[1] - a[1]
      );

    const [
      topic,
      count,
    ] = ranked[0] || [];

    if (
      topic &&
      count >= 2
    ) {
      return `🟠 Hipótesis media · Duda en ${topic}. Evidencia: revisó esa información ${count} veces y no avanzó a checkout.`.slice(
        0,
        ATTRIBUTE_VALUE_MAX
      );
    }

    if (
      variantChanges >= 3 &&
      adds === 0
    ) {
      return `🟠 Hipótesis media · Indecisión de variante/medida. Evidencia: ${variantChanges} cambios sin agregar al carrito.`;
    }

    if (
      (
        countEvent(
          'discount_interaction'
        ) +
        countEvent(
          'discount_field_focus'
        )
      ) &&
      !hasCheckout
    ) {
      return '🟡 Hipótesis posible · Sensibilidad a precio. Evidencia: revisó descuento/cupón y no avanzó a checkout.';
    }

    if (cartViews >= 2) {
      return `🟡 Hipótesis posible · Duda final antes de pagar. Evidencia: volvió al carrito ${cartViews} veces sin avanzar.`;
    }

    if (
      countEvent(
        'unavailable_variant'
      )
    ) {
      return '🟡 Hipótesis posible · Disponibilidad/variante. Evidencia: encontró al menos una opción no disponible.';
    }

    if (
      countEvent('exit_intent')
    ) {
      return '⚪ Evidencia baja · Abandono/pausa. Hay señal de salida, pero no suficiente evidencia para atribuir una objeción concreta.';
    }

    return '⚪ Sin evidencia suficiente para atribuir un bloqueo concreto. Mantener la conclusión en nivel exploratorio.';
  }

  function opportunitySummary() {
    const severity =
      frictionSeverity();

    const stage =
      deriveStageCode(
        currentSession
      );

    const hasCart =
      Number(
        currentSession.cart
          ?.item_count || 0
      ) > 0 ||
      countEvent(
        'add_to_cart'
      ) > 0;

    const topTopic =
      Object.entries(
        topicCounts()
      ).sort(
        (a, b) => b[1] - a[1]
      )[0]?.[0] || '';

    if (
      countEvent(
        'purchase_detected'
      )
    ) {
      return '✅ Compra detectada: usar este journey para identificar impulsores replicables en ficha y campañas.';
    }

    if (
      countEvent(
        'cart_request_error'
      )
    ) {
      return '🚨 Prioridad UX: corregir error técnico de carrito en la página/contexto registrado.';
    }

    if (
      countEvent(
        'add_to_cart_failed'
      )
    ) {
      return '🔧 Prioridad UX: revisar botón, variante y respuesta del flujo “Agregar al carrito”.';
    }

    if (
      countEvent(
        'checkout_stalled'
      )
    ) {
      return '🔧 Prioridad UX: revisar CTA/flujo de checkout y scripts que puedan bloquear navegación.';
    }

    if (
      countEvent(
        'form_validation_issue'
      )
    ) {
      return '🧾 Prioridad UX: simplificar o aclarar el campo que generó validación.';
    }

    if (
      severity.label === 'Alta'
    ) {
      return '🚨 Journey con fricción alta: revisar detalle y contexto antes de aumentar tráfico a esa experiencia.';
    }

    if (stage === 'checkout') {
      return '✅ Alta intención: conservar los contenidos que precedieron checkout y revisar fricciones que fueron superadas.';
    }

    if (hasCart) {
      if (
        topTopic.startsWith(
          'medidas'
        )
      ) {
        return '📏 Cerca de convertir: llevar medidas/compatibilidad junto al CTA y reducir incertidumbre antes de pagar.';
      }

      if (
        topTopic.startsWith(
          'despacho'
        )
      ) {
        return '🚚 Cerca de convertir: mostrar plazo/costo de entrega con máxima claridad en producto y carrito.';
      }

      if (
        topTopic.startsWith(
          'pago'
        )
      ) {
        return '💳 Cerca de convertir: reforzar medios de pago/cuotas junto al precio y antes del checkout.';
      }

      if (
        topTopic.startsWith(
          'stock'
        )
      ) {
        return '📦 Cerca de convertir: reforzar disponibilidad real y evitar ambigüedad de stock/variante.';
      }

      return '🎯 Cliente cerca de convertir: reforzar confianza, despacho, disponibilidad y CTA de checkout.';
    }

    if (
      countEvent(
        'discount_interaction'
      ) +
        countEvent(
          'discount_field_focus'
        ) >
      0
    ) {
      return '💰 Posible barrera de valor: destacar ahorro, beneficio vigente y razones concretas para elegir el producto.';
    }

    if (
      countEvent(
        'whatsapp_click'
      )
    ) {
      return '💬 Cliente buscó apoyo: convertir las dudas frecuentes de WhatsApp en contenido visible dentro de la ficha.';
    }

    return '👀 Aún en evaluación: mejorar claridad de beneficios, diferenciadores, compatibilidad y prueba social del producto principal.';
  }

  function rankProductsAcrossSessions() {
    const map = new Map();

    allRecentSessions().forEach(
      (session) => {
        Object.values(
          session.products || {}
        ).forEach((product) => {
          const key =
            product.handle ||
            product.id ||
            product.title;

          if (!key) return;

          const existing =
            map.get(key) || {
              key,
              title:
                cleanPageTitle(
                  product.title ||
                    product.handle ||
                    ''
                ),
              views: 0,
              active_seconds: 0,
              sessions: 0,
            };

          existing.views +=
            Number(
              product.views || 0
            );

          existing.active_seconds +=
            Number(
              product.active_seconds ||
                0
            );

          existing.sessions += 1;

          map.set(
            key,
            existing
          );
        });
      }
    );

    return [
      ...map.values(),
    ]
      .map((item) => ({
        ...item,

        affinity_score:
          Math.round(
            item.views * 30 +
              item.active_seconds +
              item.sessions * 25
          ),
      }))
      .sort(
        (a, b) =>
          b.affinity_score -
          a.affinity_score
      );
  }

  function productAffinitySummary() {
    const ranked =
      rankProductsAcrossSessions().slice(
        0,
        3
      );

    if (!ranked.length) {
      return 'Sin afinidad de producto suficiente todavía';
    }

    return ranked
      .map((item, index) => {
        return `#${index + 1} ${safeString(
          item.title,
          55
        )} · ${
          item.views
        } vistas · ${formatDuration(
          item.active_seconds
        )} · ${
          item.sessions
        } sesión${
          item.sessions === 1
            ? ''
            : 'es'
        }`;
      })
      .join(' | ')
      .slice(
        0,
        ATTRIBUTE_VALUE_MAX
      );
  }

  function lifecycleIntentScore() {
    const sessions =
      allRecentSessions();

    if (!sessions.length) {
      return Number(
        currentSession.score || 0
      );
    }

    const current =
      Number(
        currentSession.score || 0
      );

    const average =
      sessions.reduce(
        (sum, session) =>
          sum +
          Number(
            session.score || 0
          ),
        0
      ) / sessions.length;

    const max = Math.max(
      ...sessions.map(
        (session) =>
          Number(
            session.score || 0
          )
      ),
      current
    );

    let score = Math.round(
      current * 0.65 +
        average * 0.2 +
        max * 0.15
    );

    if (
      countEvent(
        'purchase_detected'
      )
    ) {
      score = 100;
    } else if (
      countEvent(
        'checkout_click'
      )
    ) {
      score = Math.max(
        score,
        90
      );
    } else if (
      Number(
        currentSession.cart
          ?.item_count || 0
      ) > 0 ||
      countEvent('add_to_cart')
    ) {
      score = Math.max(
        score,
        68
      );
    }

    return Math.max(
      0,
      Math.min(100, score)
    );
  }

  function readinessSummary() {
    const score =
      lifecycleIntentScore();

    const friction =
      frictionSeverity();

    const stage =
      deriveStageCode(
        currentSession
      );

    let label =
      score >= 85
        ? 'Muy alta'
        : score >= 68
          ? 'Alta'
          : score >= 45
            ? 'Media'
            : 'Baja';

    let risk =
      'Riesgo normal';

    if (stage === 'purchase') {
      risk =
        'Convertido detectado';
    } else if (
      score >= 68 &&
      friction.score >= 5
    ) {
      risk =
        'Alta intención + riesgo de fuga';
    } else if (
      score >= 68
    ) {
      risk =
        'Alta oportunidad';
    } else if (
      friction.score >= 5
    ) {
      risk =
        'Fricción > intención';
    }

    return `${
      stage === 'purchase'
        ? '✅'
        : score >= 68
          ? '🎯'
          : '📊'
    } Preparación ${label} · ${score}/100 · ${risk}`.slice(
      0,
      ATTRIBUTE_VALUE_MAX
    );
  }

  function behaviorPatternSummary() {
    const sessions =
      allRecentSessions();

    const ranked =
      rankProductsAcrossSessions();

    const uniqueProducts =
      ranked.length;

    const totalRepeat =
      ranked.reduce(
        (sum, item) =>
          sum +
          Math.max(
            0,
            item.views -
              item.sessions
          ),
        0
      );

    const topTopic =
      Object.entries(
        topicCounts()
      ).sort(
        (a, b) => b[1] - a[1]
      )[0]?.[0] || '';

    const variantChanges =
      countEvent(
        'variant_interaction'
      );

    const discountSignals =
      countEvent(
        'discount_interaction'
      ) +
      countEvent(
        'discount_field_focus'
      );

    const support =
      countEvent(
        'whatsapp_click'
      ) +
      countEvent(
        'showroom_click'
      );

    const main = ranked[0];

    let pattern =
      'Explorador';

    let evidence =
      'navegación inicial sin una señal dominante';

    if (
      sessions.length >= 2 &&
      main?.sessions >= 2 &&
      (
        main.views >= 3 ||
        currentSession.score >=
          50
      )
    ) {
      pattern =
        'Recurrente validando una decisión';

      evidence =
        `volvió a ${safeString(
          main.title,
          55
        )} en ${
          main.sessions
        } sesiones`;
    } else if (
      uniqueProducts >= 4 ||
      totalRepeat >= 4
    ) {
      pattern =
        'Comparador activo';

      evidence =
        `${uniqueProducts} productos y ${totalRepeat} revisitas`;
    } else if (
      variantChanges >= 3 ||
      topTopic.startsWith(
        'medidas'
      )
    ) {
      pattern =
        'Validador de compatibilidad';

      evidence =
        `${variantChanges} cambios de variante y foco en ${
          topTopic ||
          'medidas'
        }`;
    } else if (
      support ||
      topTopic.startsWith(
        'reseñas'
      ) ||
      topTopic.startsWith(
        'cambios'
      )
    ) {
      pattern =
        'Busca confianza antes de decidir';

      evidence = support
        ? 'buscó apoyo por WhatsApp/showroom'
        : `revisó ${topTopic}`;
    } else if (
      discountSignals >= 1 &&
      currentSession.score >=
        35
    ) {
      pattern =
        'Evalúa valor/precio';

      evidence =
        'interactuó con descuento/cupón durante la evaluación';
    } else if (
      deriveStageCode(
        currentSession
      ) === 'checkout' &&
      uniqueProducts <= 2
    ) {
      pattern =
        'Decisión directa';

      evidence =
        'pocas alternativas y avance rápido a checkout';
    }

    return `🧬 Patrón observado: ${pattern} · Evidencia: ${evidence}`.slice(
      0,
      ATTRIBUTE_VALUE_MAX
    );
  }

  function conversionDriverEvidence() {
    const scores = new Map();

    const targetWeights = {
      add_to_cart: 2,
      checkout_click: 3,
      purchase_detected: 4,
    };

    const targetLabels = {
      add_to_cart: 'agregar',
      checkout_click: 'checkout',
      purchase_detected: 'compra',
    };

    const driverTopic = (
      event
    ) => {
      if (
        event.type ===
        'info_interest'
      ) {
        return safeString(
          event.details?.topic,
          60
        );
      }

      if (
        [
          'discount_interaction',
          'discount_field_focus',
        ].includes(event.type)
      ) {
        return 'precio/descuento';
      }

      if (
        event.type ===
        'variant_interaction'
      ) {
        return 'variante/medida';
      }

      if (
        event.type ===
        'whatsapp_click'
      ) {
        return 'soporte/WhatsApp';
      }

      if (
        event.type ===
        'showroom_click'
      ) {
        return 'showroom/confianza';
      }

      return '';
    };

    allRecentSessions().forEach(
      (session) => {
        const events =
          session.events || [];

        events.forEach(
          (
            target,
            targetIndex
          ) => {
            const targetWeight =
              targetWeights[
                target.type
              ];

            if (!targetWeight) {
              return;
            }

            const targetTime =
              Date.parse(
                target.timestamp ||
                  ''
              ) || 0;

            const seenTopics =
              new Set();

            for (
              let index =
                targetIndex - 1;
              index >= 0;
              index -= 1
            ) {
              const event =
                events[index];

              const eventTime =
                Date.parse(
                  event.timestamp ||
                    ''
                ) || 0;

              const delta =
                targetTime -
                eventTime;

              if (delta < 0) {
                continue;
              }

              if (
                delta >
                DRIVER_WINDOW_MS
              ) {
                break;
              }

              const topic =
                driverTopic(event);

              if (
                !topic ||
                seenTopics.has(
                  topic
                )
              ) {
                continue;
              }

              seenTopics.add(topic);

              const recencyBoost =
                delta <= 60_000
                  ? 2
                  : delta <=
                      180_000
                    ? 1
                    : 0;

              const item =
                scores.get(topic) || {
                  topic,
                  score: 0,
                  count: 0,
                  targets:
                    new Set(),
                };

              item.score +=
                targetWeight +
                recencyBoost;

              item.count += 1;

              item.targets.add(
                targetLabels[
                  target.type
                ]
              );

              scores.set(
                topic,
                item
              );
            }
          }
        );
      }
    );

    return [
      ...scores.values(),
    ]
      .map((item) => ({
        ...item,
        targets: [
          ...item.targets,
        ],
      }))
      .sort(
        (a, b) =>
          b.score - a.score ||
          b.count - a.count
      );
  }

  function conversionDriverSummary() {
    const drivers =
      conversionDriverEvidence().slice(
        0,
        2
      );

    if (!drivers.length) {
      return '⚪ Sin impulsor atribuible con evidencia temporal suficiente';
    }

    return drivers
      .map((item, index) => {
        const confidence =
          item.score >= 8
            ? 'Alta'
            : item.score >= 4
              ? 'Media'
              : 'Posible';

        return `${
          index === 0
            ? '🚀'
            : '•'
        } #${
          index + 1
        } ${confidence}: ${
          item.topic
        } · antes de ${item.targets.join(
          '/'
        )} · ${
          item.count
        } coincidencia${
          item.count === 1
            ? ''
            : 's'
        }`;
      })
      .join(' | ')
      .slice(
        0,
        ATTRIBUTE_VALUE_MAX
      );
  }

  function stageTimingSummary() {
    const history =
      currentSession.stage_history ||
      [];

    const start =
      Date.parse(
        currentSession.started_at ||
          ''
      ) || 0;

    if (
      !start ||
      !history.length
    ) {
      return 'Sin tiempos de etapa suficientes';
    }

    const seen = new Set();
    const parts = [];

    history.forEach((entry) => {
      if (
        !entry?.stage ||
        seen.has(entry.stage)
      ) {
        return;
      }

      seen.add(entry.stage);

      const at =
        Date.parse(
          entry.timestamp || ''
        ) || start;

      const seconds =
        Math.max(
          0,
          Math.round(
            (at - start) / 1000
          )
        );

      if (
        entry.stage ===
          'discovery' &&
        seconds === 0
      ) {
        return;
      }

      parts.push(
        `${stageLabel(
          entry.stage
        )} +${formatDuration(
          seconds
        )}`
      );
    });

    return parts.length
      ? `⏱️ ${parts.join(
          ' → '
        )}`.slice(
          0,
          ATTRIBUTE_VALUE_MAX
        )
      : '⏱️ Aún sin transición de etapa';
  }

  function attentionSummary() {
    const entries =
      Object.entries(
        currentSession.page_active_seconds ||
          {}
      )
        .sort(
          (a, b) =>
            Number(b[1]) -
            Number(a[1])
        )
        .slice(0, 3);

    if (!entries.length) {
      return 'Sin mapa de atención suficiente';
    }

    return entries
      .map(
        (
          [path, seconds],
          index
        ) =>
          `${index + 1}. ${pageLabelForPath(
            path
          )} · ${formatDuration(
            seconds
          )}`
      )
      .join(' | ')
      .slice(
        0,
        ATTRIBUTE_VALUE_MAX
      );
  }

  function technicalExperienceSummary() {
    const perf =
      currentSession.performance ||
      {};

    const metrics = [];

    if (
      Number(perf.lcp_ms || 0)
    ) {
      metrics.push(
        `LCP ${Math.round(
          perf.lcp_ms
        )}ms`
      );
    }

    if (
      Number(perf.inp_ms || 0)
    ) {
      metrics.push(
        `INP ${Math.round(
          perf.inp_ms
        )}ms`
      );
    }

    if (
      Number(perf.cls || 0)
    ) {
      metrics.push(
        `CLS ${Number(
          perf.cls
        ).toFixed(2)}`
      );
    }

    if (
      Number(perf.fcp_ms || 0)
    ) {
      metrics.push(
        `FCP ${Math.round(
          perf.fcp_ms
        )}ms`
      );
    }

    if (
      Number(perf.ttfb_ms || 0)
    ) {
      metrics.push(
        `TTFB ${Math.round(
          perf.ttfb_ms
        )}ms`
      );
    }

    const issues =
      performanceFrictionSignals();

    const jsErrors =
      countEvent('js_error');

    const cartErrors =
      countEvent(
        'cart_request_error'
      );

    if (
      !metrics.length &&
      !jsErrors &&
      !cartErrors
    ) {
      return '⚪ Sin métricas de rendimiento disponibles en este navegador';
    }

    const grade =
      issues.length >= 2
        ? '🔴 Experiencia técnica crítica'
        : issues.length === 1
          ? '🟠 Experiencia técnica a revisar'
          : '🟢 Experiencia técnica sin señal grave';

    const thresholds = {
      lcp_ms: 2500,
      inp_ms: 200,
      cls: 0.1,
      fcp_ms: 1800,
      ttfb_ms: 800,
    };

    const worst =
      Object.entries(
        currentSession.performance_worst ||
          {}
      )
        .map(
          ([metric, data]) => ({
            metric,
            data,
            ratio:
              Number(
                data?.value || 0
              ) /
              Number(
                thresholds[
                  metric
                ] || 1
              ),
          })
        )
        .sort(
          (a, b) =>
            b.ratio -
            a.ratio
        )[0];

    const worstText =
      worst?.data?.path
        ? ` · Peor contexto: ${worst.metric
            .toUpperCase()
            .replace(
              '_MS',
              ''
            )} en ${pageLabelForPath(
            worst.data.path
          )}`
        : '';

    return `${grade} · ${metrics.join(
      ' · '
    )}${
      jsErrors
        ? ` · ${jsErrors} error JS`
        : ''
    }${
      cartErrors
        ? ` · ${cartErrors} error carrito`
        : ''
    }${worstText}`.slice(
      0,
      ATTRIBUTE_VALUE_MAX
    );
  }

  function frictionStageSummary() {
    const frictionTypes =
      new Set([
        'cart_request_error',
        'add_to_cart_failed',
        'checkout_stalled',
        'rage_click',
        'js_error',
        'remove_from_cart',
        'form_validation_issue',
        'unavailable_variant',
        'exit_intent',
      ]);

    const counts = {};

    (
      currentSession.events || []
    ).forEach((event) => {
      if (
        !frictionTypes.has(
          event.type
        )
      ) {
        return;
      }

      const stage =
        event.page_type ||
        'page';

      counts[stage] =
        (counts[stage] || 0) + 1;
    });

    const parts =
      Object.entries(counts)
        .sort(
          (a, b) =>
            b[1] - a[1]
        )
        .map(
          ([stage, count]) =>
            `${stage}: ${count}`
        );

    return parts.length
      ? `📍 Fricción por contexto · ${parts.join(
          ' · '
        )}`.slice(
          0,
          ATTRIBUTE_VALUE_MAX
        )
      : '📍 Sin concentración de fricción por etapa';
  }

  function commerceValueSummary() {
    const currentValue =
      Number(
        currentSession.cart
          ?.total_price || 0
      );

    const peakValue =
      Number(
        currentSession.peak_cart_total ||
          currentValue
      );

    const removedValue =
      Number(
        currentSession.removed_cart_value ||
          0
      );

    const currentItems =
      Number(
        currentSession.cart
          ?.item_count || 0
      );

    const peakItems =
      Number(
        currentSession.peak_cart_items ||
          currentItems
      );

    return [
      `Actual ${formatMoneyCents(
        currentValue
      )} (${currentItems} ítem${
        currentItems === 1
          ? ''
          : 's'
      })`,

      `Pico ${formatMoneyCents(
        peakValue
      )} (${peakItems} ítem${
        peakItems === 1
          ? ''
          : 's'
      })`,

      removedValue
        ? `Reconsiderado/eliminado ${formatMoneyCents(
            removedValue
          )}`
        : '',
    ]
      .filter(Boolean)
      .join(' · ')
      .slice(
        0,
        ATTRIBUTE_VALUE_MAX
      );
  }

  function uxActionSummary() {
    const topTopic =
      Object.entries(
        topicCounts()
      ).sort(
        (a, b) => b[1] - a[1]
      )[0]?.[0] || '';

    if (
      countEvent(
        'cart_request_error'
      ) ||
      countEvent(
        'add_to_cart_failed'
      )
    ) {
      return '🔧 UX: corregir primero el flujo de carrito/agregado; es una fricción observable y de alta prioridad.';
    }

    if (
      countEvent(
        'checkout_stalled'
      )
    ) {
      return '🔧 UX: auditar botón de checkout, scripts, overlays y navegación desde carrito.';
    }

    if (
      performanceFrictionSignals()
        .length
    ) {
      return `🐢 UX: optimizar rendimiento en la página observada (${performanceFrictionSignals()
        .slice(0, 2)
        .join(', ')}).`;
    }

    if (
      topTopic.startsWith(
        'medidas'
      )
    ) {
      return '📏 UX: mostrar medidas/compatibilidad cerca del CTA, con guía visual y respuesta “¿me sirve?” inmediata.';
    }

    if (
      topTopic.startsWith(
        'despacho'
      )
    ) {
      return '🚚 UX: mostrar plazo y condiciones de despacho cerca del precio/CTA, antes del carrito.';
    }

    if (
      topTopic.startsWith(
        'pago'
      )
    ) {
      return '💳 UX: hacer visibles cuotas/medios de pago junto al precio y CTA.';
    }

    if (
      topTopic.startsWith(
        'stock'
      )
    ) {
      return '📦 UX: hacer inequívoca la disponibilidad por variante y evitar opciones aparentemente seleccionables sin stock.';
    }

    if (
      topTopic.startsWith(
        'reseñas'
      )
    ) {
      return '⭐ UX: acercar reseñas/prueba social al bloque de decisión y CTA.';
    }

    if (
      topTopic.startsWith(
        'seguridad'
      )
    ) {
      return '🛡️ UX: reforzar seguridad, uso y especificaciones clave antes del CTA.';
    }

    return opportunitySummary();
  }

  function remarketingAngleSummary() {
    if (
      countEvent(
        'purchase_detected'
      )
    ) {
      return '✅ Remarketing: no priorizar recuperación; compra detectada.';
    }

    const main =
      mainProductAcrossSessions();

    const topic =
      Object.entries(
        topicCounts()
      ).sort(
        (a, b) => b[1] - a[1]
      )[0]?.[0] || '';

    const cart =
      Number(
        currentSession.cart
          ?.item_count || 0
      ) > 0;

    let angle = cart
      ? 'Retoma tu elección + confianza para cerrar'
      : 'Beneficio principal + prueba social';

    if (
      topic.startsWith(
        'medidas'
      )
    ) {
      angle =
        'Compatibilidad sin dudas + medidas claras';
    } else if (
      topic.startsWith(
        'despacho'
      )
    ) {
      angle =
        'Despacho claro/rápido + cuándo llega';
    } else if (
      topic.startsWith(
        'pago'
      )
    ) {
      angle =
        'Facilidad de pago + cuotas/medios disponibles';
    } else if (
      topic.startsWith(
        'stock'
      )
    ) {
      angle =
        'Disponibilidad real + variante correcta';
    } else if (
      topic.startsWith(
        'reseñas'
      )
    ) {
      angle =
        'Confianza + reseñas de otros padres';
    } else if (
      topic.startsWith(
        'seguridad'
      )
    ) {
      angle =
        'Seguridad + tranquilidad + uso real';
    } else if (
      countEvent(
        'discount_interaction'
      ) +
        countEvent(
          'discount_field_focus'
        ) >
      0
    ) {
      angle =
        'Valor + ahorro + beneficio concreto';
    }

    return `📣 Ángulo sugerido: ${angle}${
      main?.title
        ? ` · Producto: ${safeString(
            main.title,
            62
          )}`
        : ''
    }`.slice(
      0,
      ATTRIBUTE_VALUE_MAX
    );
  }

  function overallCustomerDiagnosis() {
    const readiness =
      lifecycleIntentScore();

    const friction =
      frictionSeverity();

    const stage =
      deriveStageCode(
        currentSession
      );

    if (stage === 'purchase') {
      return '✅ Convertido detectado · estudiar impulsores y replicarlos';
    }

    if (
      stage === 'checkout' &&
      friction.score < 5
    ) {
      return '🔥 Conversión muy avanzada · journey sano hasta checkout';
    }

    if (
      readiness >= 68 &&
      friction.score >= 5
    ) {
      return '🚨 Alta intención en riesgo · prioridad máxima de recuperación/UX';
    }

    if (readiness >= 68) {
      return '🎯 Alta oportunidad · falta cerrar la decisión';
    }

    if (
      friction.score >= 5
    ) {
      return '⚠️ Fricción relevante con intención media/baja';
    }

    if (readiness >= 45) {
      return '🟡 Consideración activa · necesita más evidencia para decidir';
    }

    return '🔵 Exploración temprana · aún sin intención comercial fuerte';
  }

  function orderAttributeKey(label) {
    return PRIVATE_ORDER_ATTRIBUTES
      ? `${PRIVATE_ATTRIBUTE_PREFIX} ${label}`
      : label;
  }

  function buildOrderAttributes(
    entries
  ) {
    return cleanObject(
      Object.fromEntries(
        entries.map(
          ([label, value]) => [
            orderAttributeKey(
              label
            ),
            safeString(
              value == null
                ? '-'
                : value,
              ATTRIBUTE_VALUE_MAX
            ),
          ]
        )
      )
    );
  }

  function routeSummary() {
    const pages =
      currentSession.important_pages ||
      [];

    const route = [];

    pages
      .slice(-14)
      .forEach((page) => {
        const name =
          pageName(page);

        if (
          name &&
          route[
            route.length - 1
          ] !== name
        ) {
          route.push(name);
        }
      });

    return route.length
      ? route
          .join(' → ')
          .slice(0, 240)
      : 'Sin recorrido registrado';
  }

  function eventDetail(event) {
    const details =
      event.details || {};

    const time =
      formatDateTime(
        event.timestamp
      );

    const product =
      cleanPageTitle(
        details.title ||
          details.product?.title ||
          details.product_title ||
          ''
      );

    const labels = {
      product_view: product
        ? `Vio ${product}`
        : 'Vio producto',

      collection_view:
        details.title
          ? `Abrió ${cleanPageTitle(
              details.title
            )}`
          : 'Abrió colección',

      search_view:
        details.term
          ? `Buscó “${safeString(
              details.term,
              60
            )}”`
          : 'Abrió búsqueda',

      search_submit:
        details.term
          ? `Buscó “${safeString(
              details.term,
              60
            )}”`
          : 'Realizó búsqueda',

      cart_view:
        'Abrió carrito',

      add_to_cart_intent:
        'Presionó agregar al carrito',

      cart_request_error:
        `Error de carrito (${safeString(
          details.action ||
            'operación',
          20
        )} · HTTP ${
          details.status ||
          'red'
        })`,

      add_to_cart_failed:
        'Intentó agregar al carrito, pero no se confirmó el cambio',

      add_to_cart: product
        ? `Agregó ${product}`
        : 'Agregó producto',

      remove_from_cart:
        product
          ? `Eliminó ${product}`
          : 'Eliminó producto',

      quantity_change:
        product
          ? `Cambió ${product} de ${details.previous_quantity} a ${details.new_quantity}`
          : 'Cambió cantidad',

      checkout_click:
        'Avanzó al checkout',

      checkout_stalled:
        'Intentó checkout pero permaneció en carrito',

      whatsapp_click:
        'Abrió WhatsApp',

      showroom_click:
        'Abrió showroom',

      discount_interaction:
        'Interactuó con descuento',

      discount_field_focus:
        'Revisó el campo de descuento/cupón',

      info_interest:
        details.topic
          ? `Revisó información de ${safeString(
              details.topic,
              45
            )}`
          : 'Revisó información del producto',

      variant_interaction:
        details.option_value
          ? `Probó variante ${safeString(
              details.option_value,
              45
            )}`
          : 'Interactuó con variante',

      unavailable_variant:
        'Encontró una variante no disponible',

      form_validation_issue:
        'Tuvo una validación de formulario',

      purchase_detected:
        'Compra confirmada detectada',

      rage_click:
        'Clics repetitivos / posible frustración',

      exit_intent:
        'Mostró intención de salir',

      js_error:
        `Error: ${safeString(
          details.message,
          80
        )}`,

      scroll_depth:
        `Llegó al ${
          details.percent || 0
        }% de la página`,
    };

    const label =
      labels[event.type];

    return label
      ? `${time} ${label}`
      : '';
  }

  function timelineText() {
    const entries = [];

    state.sessions
      .slice(-5)
      .forEach(
        (
          session,
          sessionIndex
        ) => {
          entries.push(
            `S${
              sessionIndex + 1
            } ${formatDateTime(
              session.started_at
            )}`
          );

          (
            session.events || []
          )
            .filter(
              (event) =>
                ![
                  'scroll_depth',
                ].includes(
                  event.type
                ) ||
                Number(
                  event.details
                    ?.percent
                ) >= 75
            )
            .slice(-35)
            .forEach(
              (event) => {
                const label =
                  eventDetail(
                    event
                  );

                if (label) {
                  entries.push(
                    label
                  );
                }
              }
            );
        }
      );

    return entries
      .slice(-45)
      .join(' → ');
  }

  function splitText(
    text,
    maxLength = 235,
    maxParts = 4
  ) {
    const source =
      safeString(
        text || '-',
        maxLength *
          maxParts
      );

    const parts = [];
    let remaining = source;

    while (
      remaining &&
      parts.length < maxParts
    ) {
      if (
        remaining.length <=
        maxLength
      ) {
        parts.push(remaining);
        break;
      }

      let cut =
        remaining.lastIndexOf(
          ' → ',
          maxLength
        );

      if (
        cut <
        Math.round(
          maxLength * 0.55
        )
      ) {
        cut =
          remaining.lastIndexOf(
            ' · ',
            maxLength
          );
      }

      if (
        cut <
        Math.round(
          maxLength * 0.55
        )
      ) {
        cut =
          remaining.lastIndexOf(
            ' | ',
            maxLength
          );
      }

      if (
        cut <
        Math.round(
          maxLength * 0.55
        )
      ) {
        cut = maxLength;
      }

      parts.push(
        remaining
          .slice(0, cut)
          .trim()
      );

      remaining = remaining
        .slice(cut)
        .replace(
          /^(\s*(→|·|\|)\s*)/,
          ''
        )
        .trim();
    }

    return parts;
  }

  function sessionSummaryText() {
    return state.sessions
      .slice(-5)
      .map(
        (session, index) => {
          const touch =
            session.entry_touch ||
            {};

          const products =
            Object.keys(
              session.products ||
                {}
            ).length;

          return [
            `S${index + 1}`,
            formatDateTime(
              session.started_at
            ),
            sourceName(
              touch.source
            ),
            `${
              session.page_count ||
              0
            } pág`,
            `${products} prod`,
            formatDuration(
              session.active_seconds ||
                0
            ),
            `${
              session.label ||
              'Frío'
            } ${
              session.score || 0
            }/100`,
          ].join(' · ');
        }
      )
      .join(' | ')
      .slice(0, 700);
  }

  function acquisitionSummary(touch) {
    return [
      sourceName(touch?.source),

      touch?.medium
        ? `Medio: ${safeString(
            touch.medium,
            50
          )}`
        : '',

      touch?.campaign
        ? `Campaña: ${safeString(
            touch.campaign,
            80
          )}`
        : '',

      touch?.adset
        ? `Conjunto: ${safeString(
            touch.adset,
            70
          )}`
        : '',

      touch?.placement
        ? `Ubicación: ${safeString(
            touch.placement,
            60
          )}`
        : '',

      touch?.landing_path
        ? `Entrada: ${safeString(
            touch.landing_path,
            80
          )}`
        : '',
    ]
      .filter(Boolean)
      .join(' · ')
      .slice(0, 240);
  }

  function creativeSummary(touch) {
    const details = [
      touch?.ad
        ? `Anuncio: ${safeString(
            touch.ad,
            90
          )}`
        : '',

      touch?.format
        ? `Formato: ${safeString(
            touch.format,
            50
          )}`
        : '',

      touch?.angle
        ? `Ángulo: ${safeString(
            touch.angle,
            60
          )}`
        : '',

      touch?.hook
        ? `Hook: ${safeString(
            touch.hook,
            80
          )}`
        : '',
    ].filter(Boolean);

    return details.length
      ? details
          .join(' · ')
          .slice(0, 240)
      : 'Sin datos específicos del creativo';
  }

  function customerProfileSummary() {
    const sessions =
      state.sessions.length;

    const type =
      sessions >= 3
        ? `🔁 Recurrente · ${sessions} sesiones`
        : sessions === 2
          ? '🔄 Segunda visita'
          : '✨ Primera visita';

    const emoji =
      currentSession.label ===
      'Muy caliente'
        ? '🔥'
        : currentSession.label ===
            'Caliente'
          ? '🟠'
          : currentSession.label ===
              'Tibio'
            ? '🟡'
            : '🔵';

    return `${emoji} ${currentSession.label} · ${currentSession.score}/100 · ${type}`;
  }

  function executiveCustomerSummary() {
    const main =
      mainProductAcrossSessions();

    const severity =
      frictionSeverity();

    const stage =
      stageLabel(
        deriveStageCode(
          currentSession
        )
      );

    const readiness =
      lifecycleIntentScore();

    const cartValue =
      Number(
        currentSession.cart
          ?.total_price || 0
      );

    return [
      overallCustomerDiagnosis(),
      `Preparación ${readiness}/100`,
      `Etapa: ${stage}`,

      main?.title
        ? `Interés: ${safeString(
            main.title,
            58
          )}`
        : '',

      `Fricción: ${severity.label}`,

      cartValue
        ? `Carrito: ${formatMoneyCents(
            cartValue
          )}`
        : '',
    ]
      .filter(Boolean)
      .join(' · ')
      .slice(
        0,
        ATTRIBUTE_VALUE_MAX
      );
  }

  function customerConclusionText() {
    const sessions =
      allRecentSessions();

    const main =
      mainProductAcrossSessions();

    const stage =
      stageLabel(
        deriveStageCode(
          currentSession
        )
      );

    const confidence =
      evidenceConfidence();

    const readiness =
      lifecycleIntentScore();

    const friction =
      frictionSeverity();

    const drivers =
      conversionDriverEvidence();

    const topDriver =
      drivers[0];

    const cartCount =
      Number(
        currentSession.cart
          ?.item_count || 0
      );

    const cartValue =
      Number(
        currentSession.cart
          ?.total_price || 0
      );

    const hasPurchase =
      countEvent(
        'purchase_detected'
      ) > 0;

    const hasCheckout =
      countEvent(
        'checkout_click'
      ) > 0;

    const profile =
      `Diagnóstico: ${overallCustomerDiagnosis().replace(
        /^[^\wÁÉÍÓÚÑáéíóúñ]+/u,
        ''
      )}. ${
        sessions.length > 1
          ? `Cliente recurrente con ${sessions.length} sesiones`
          : 'Primera sesión registrada'
      }; preparación ${readiness}/100 y etapa ${stage.toLowerCase()}.`;

    const interest =
      main?.title
        ? `Interés dominante: ${safeString(
            main.title,
            72
          )} · ${
            main.views
          } vista${
            main.views === 1
              ? ''
              : 's'
          } · ${formatDuration(
            main.active_seconds
          )} · ${
            main.sessions
          } sesión${
            main.sessions === 1
              ? ''
              : 'es'
          }.`
        : 'Interés dominante: todavía no existe evidencia suficiente para priorizar un producto.';

    const pattern =
      `${behaviorPatternSummary().replace(
        /^[^\wÁÉÍÓÚÑáéíóúñ]+/u,
        ''
      )}.`;

    const driver =
      topDriver
        ? `Posible impulsor de avance: ${topDriver.topic}; apareció antes de ${topDriver.targets.join(
            '/'
          )} con ${
            topDriver.count
          } asociación${
            topDriver.count === 1
              ? ''
              : 'es'
          }. No implica causalidad, pero es la señal positiva mejor respaldada.`
        : 'Impulsor de avance: no hay una señal temporal suficientemente fuerte para atribuir qué facilitó la decisión.';

    const blocker =
      `Bloqueo/objeción: ${probableBlockerSummary().replace(
        /^[^\wÁÉÍÓÚÑáéíóúñ]+/u,
        ''
      )}`;

    const tech =
      performanceFrictionSignals()
        .length ||
      countEvent('js_error') ||
      countEvent(
        'cart_request_error'
      )
        ? `Experiencia técnica: ${technicalExperienceSummary().replace(
            /^[^\wÁÉÍÓÚÑáéíóúñ]+/u,
            ''
          )}.`
        : `Experiencia técnica: sin señal grave registrada; fricción conductual ${friction.label.toLowerCase()}.`;

    const outcome =
      hasPurchase
        ? 'Cierre observado: compra confirmada detectada.'
        : hasCheckout
          ? 'Cierre observado: alcanzó checkout; el storefront no confirma por sí solo si el pago terminó correctamente.'
          : cartCount
            ? `Cierre observado: dejó ${cartCount} producto${
                cartCount === 1
                  ? ''
                  : 's'
              } por ${formatMoneyCents(
                cartValue
              )} en carrito sin señal de checkout.`
            : readiness >= 50
              ? 'Cierre observado: consideración activa/avanzada sin llegar a checkout.'
              : 'Cierre observado: exploración temprana sin decisión comercial fuerte.';

    const action =
      `Siguiente mejor acción: ${uxActionSummary().replace(
        /^[^\wÁÉÍÓÚÑáéíóúñ]+/u,
        ''
      )}`;

    const remarketing =
      `${remarketingAngleSummary().replace(
        /^[^\wÁÉÍÓÚÑáéíóúñ]+/u,
        ''
      )}.`;

    const quality =
      `Confianza de la conclusión: ${confidence.label.toLowerCase()} (${confidence.score} pts de evidencia). Las causas inferidas se mantienen como hipótesis y no como hechos.`;

    return [
      profile,
      interest,
      pattern,
      driver,
      blocker,
      tech,
      outcome,
      action,
      remarketing,
      quality,
    ]
      .filter(Boolean)
      .join(' ');
  }

  function cartAttributes() {
    const first =
      state.first_marketing_touch ||
      state.first_touch ||
      preferredTouch();

    const last =
      state.last_touch || first;

    const timeline =
      splitText(
        timelineText(),
        ATTRIBUTE_VALUE_MAX,
        4
      );

    const sessions =
      splitText(
        sessionSummaryText(),
        ATTRIBUTE_VALUE_MAX,
        3
      );

    const frictionDetail =
      splitText(
        frictionDetailText(),
        ATTRIBUTE_VALUE_MAX,
        3
      );

    const conclusion =
      splitText(
        customerConclusionText(),
        ATTRIBUTE_VALUE_MAX,
        6
      );

    const identifiers = [
      `Visitor: ${safeString(
        state.visitor_id,
        55
      )}`,

      `Sesión: ${safeString(
        currentSession.id,
        55
      )}`,

      first?.campaign_id
        ? `Campaña ID: ${safeString(
            first.campaign_id,
            45
          )}`
        : '',

      first?.ad_id
        ? `Anuncio ID: ${safeString(
            first.ad_id,
            45
          )}`
        : '',

      first?.gclid
        ? `GCLID: ${safeString(
            first.gclid,
            65
          )}`
        : '',

      first?.fbclid
        ? `FBCLID: ${safeString(
            first.fbclid,
            65
          )}`
        : '',
    ]
      .filter(Boolean)
      .join(' · ');

    return buildOrderAttributes([
      [
        '00 🧭 DIAGNÓSTICO EJECUTIVO',
        executiveCustomerSummary(),
      ],
      [
        '01 🎯 PERFIL E INTENCIÓN',
        customerProfileSummary(),
      ],
      [
        '02 🧠 PREPARACIÓN PARA COMPRAR',
        readinessSummary(),
      ],
      [
        '03 🧬 PATRÓN DE COMPORTAMIENTO',
        behaviorPatternSummary(),
      ],
      [
        '04 🚦 ETAPA DEL JOURNEY',
        journeyStageSummary(),
      ],
      [
        '05 ⏱️ TIEMPO ENTRE ETAPAS',
        stageTimingSummary(),
      ],
      [
        '06 📈 EVOLUCIÓN ENTRE SESIONES',
        customerEvolutionSummary(),
      ],
      [
        '07 📣 PRIMER ORIGEN',
        acquisitionSummary(first),
      ],
      [
        '08 🔄 ÚLTIMO ORIGEN',
        acquisitionSummary(last),
      ],
      [
        '09 🎨 CREATIVO DE ENTRADA',
        creativeSummary(first),
      ],
      [
        '10 👀 PRODUCTO DE MAYOR INTERÉS',
        productSummary(),
      ],
      [
        '11 ❤️ AFINIDAD DE PRODUCTOS',
        productAffinitySummary(),
      ],
      [
        '12 🚀 POSIBLES IMPULSORES DE CONVERSIÓN',
        conversionDriverSummary(),
      ],
      [
        '13 🧠 LECTURA DE DECISIÓN',
        decisionSignalsSummary(),
      ],
      [
        '14 ❓ ÁREAS DE DUDA / OBJECIÓN',
        objectionAreasSummary(),
      ],
      [
        '15 👁️ MAPA DE ATENCIÓN',
        attentionSummary(),
      ],
      [
        '16 🛒 CARRITO FINAL',
        cartItemsSummary(),
      ],
      [
        '17 💰 VALOR DEL JOURNEY',
        commerceValueSummary(),
      ],
      [
        '18 ⚡ ACCIONES COMERCIALES',
        actionSummary(),
      ],
      [
        '19 🔎 BÚSQUEDAS',
        searchSummary(),
      ],
      [
        '20 📊 COMPORTAMIENTO',
        behaviorSummary(),
      ],
      [
        '21 🧪 EXPERIENCIA TÉCNICA',
        technicalExperienceSummary(),
      ],
      [
        '22 ⚠️ FRICCIONES - RESUMEN',
        frictionSummary(),
      ],
      [
        '23 📍 FRICCIÓN POR CONTEXTO',
        frictionStageSummary(),
      ],
      [
        '24 🔍 FRICCIONES - DETALLE 1',
        frictionDetail[0] || '-',
      ],
      [
        '25 🔍 FRICCIONES - DETALLE 2',
        frictionDetail[1] || '-',
      ],
      [
        '26 🔍 FRICCIONES - DETALLE 3',
        frictionDetail[2] || '-',
      ],
      [
        '27 📍 PUNTO DONDE TERMINÓ',
        stoppingPointSummary(),
      ],
      [
        '28 🎯 HIPÓTESIS PRINCIPAL DE BLOQUEO',
        probableBlockerSummary(),
      ],
      [
        '29 💡 SIGUIENTE MEJOR ACCIÓN',
        uxActionSummary(),
      ],
      [
        '30 📣 ÁNGULO DE REMARKETING',
        remarketingAngleSummary(),
      ],
      [
        '31 🧭 RECORRIDO',
        routeSummary(),
      ],
      [
        '32 ✅ SEÑALES DE INTENCIÓN',
        (currentSession.reasons || [])
          .map(
            (item) =>
              `• ${item}`
          )
          .join(' · ') ||
          'Sin señales fuertes todavía',
      ],
      [
        '33 🕒 CRONOLOGÍA 1',
        timeline[0] || '-',
      ],
      [
        '34 🕒 CRONOLOGÍA 2',
        timeline[1] || '-',
      ],
      [
        '35 🕒 CRONOLOGÍA 3',
        timeline[2] || '-',
      ],
      [
        '36 🕒 CRONOLOGÍA 4',
        timeline[3] || '-',
      ],
      [
        '37 📚 SESIONES 1',
        sessions[0] || '-',
      ],
      [
        '38 📚 SESIONES 2',
        sessions[1] || '-',
      ],
      [
        '39 📚 SESIONES 3',
        sessions[2] || '-',
      ],
      [
        '40 🔗 IDENTIFICADORES',
        identifiers,
      ],
      [
        '41 🔬 CONFIANZA DE LA LECTURA',
        readingQualitySummary(),
      ],
      [
        '42 🧩 CONCLUSIÓN DEL CLIENTE 1',
        conclusion[0] || '-',
      ],
      [
        '43 🧩 CONCLUSIÓN DEL CLIENTE 2',
        conclusion[1] || '-',
      ],
      [
        '44 🧩 CONCLUSIÓN DEL CLIENTE 3',
        conclusion[2] || '-',
      ],
      [
        '45 🧩 CONCLUSIÓN DEL CLIENTE 4',
        conclusion[3] || '-',
      ],
      [
        '46 🧩 CONCLUSIÓN DEL CLIENTE 5',
        conclusion[4] || '-',
      ],
      [
        '47 🧩 CONCLUSIÓN DEL CLIENTE 6',
        conclusion[5] || '-',
      ],
    ]);
  }

  async function syncCartAttributes(
    forceKeepalive = false
  ) {
    if (
      !originalFetch ||
      internalCartUpdate ||
      destroyed
    ) {
      return;
    }

    const cart =
      cartSnapshot ||
      (await readCart());

    if (
      !cart ||
      cart.item_count <= 0
    ) {
      return;
    }

    internalCartUpdate = true;

    try {
      await originalFetch(
        `${
          window.Shopify?.routes
            ?.root || '/'
        }cart/update.js`,
        {
          method: 'POST',

          headers: {
            'Content-Type':
              'application/json',
            Accept:
              'application/json',
            'X-BE-Tracking': '1',
          },

          credentials:
            'same-origin',

          keepalive:
            forceKeepalive,

          body: JSON.stringify({
            attributes:
              cartAttributes(),
          }),
        }
      );
    } catch (error) {
      log(
        'No se pudo actualizar atributos del carrito:',
        error
      );
    } finally {
      internalCartUpdate = false;
    }
  }

  function scheduleCartSync() {
    window.clearTimeout(
      cartSyncTimer
    );

    cartSyncTimer =
      window.setTimeout(
        () =>
          syncCartAttributes(
            false
          ),
        CART_SYNC_DELAY_MS
      );
  }

  function scheduleFirebaseSync() {
    // Sin base externa:
    // el detalle se conserva localmente
    // y el resumen se adjunta al carrito.
  }

  async function syncAllToFirebase() {
    return false;
  }

  function flushPendingClicksSoon() {
    // Desactivado en la versión
    // exclusiva para Shopify.
  }

  function trackErrors() {
    let errorCount = 0;

    window.addEventListener(
      'error',
      (event) => {
        if (
          errorCount >= 5
        ) {
          return;
        }

        errorCount += 1;

        trackEvent(
          'js_error',
          {
            message: safeString(
              event.message,
              180
            ),

            file: safeString(
              event.filename,
              180
            ),

            line:
              event.lineno || 0,
          },
          {
            cart: false,
            dataLayer: false,
            clarity: false,
          }
        );
      }
    );

    window.addEventListener(
      'unhandledrejection',
      (event) => {
        if (
          errorCount >= 5
        ) {
          return;
        }

        errorCount += 1;

        trackEvent(
          'js_error',
          {
            message: safeString(
              event.reason?.message ||
                event.reason ||
                'Unhandled promise rejection',
              180
            ),
          },
          {
            cart: false,
            dataLayer: false,
            clarity: false,
          }
        );
      }
    );
  }

  function trackExitIntent() {
    let recorded = false;

    document.addEventListener(
      'mouseout',
      (event) => {
        if (
          recorded ||
          deviceInfo().category !==
            'desktop'
        ) {
          return;
        }

        if (
          event.clientY <= 0 &&
          !event.relatedTarget
        ) {
          recorded = true;

          trackEvent(
            'exit_intent',
            {},
            {
              cart: false,
            }
          );
        }
      }
    );
  }

  function updatePerformanceMetric(
    name,
    value,
    mode = 'max'
  ) {
    if (
      !currentSession ||
      !Number.isFinite(
        Number(value)
      )
    ) {
      return;
    }

    currentSession.performance =
      currentSession.performance || {
        ttfb_ms: 0,
        fcp_ms: 0,
        lcp_ms: 0,
        cls: 0,
        inp_ms: 0,
      };

    currentSession.performance_worst =
      currentSession.performance_worst ||
      {};

    const numeric =
      Number(value);

    const previous =
      Number(
        currentSession.performance[
          name
        ] || 0
      );

    if (mode === 'sum') {
      currentSession.performance[
        name
      ] =
        previous + numeric;

      return;
    }

    if (numeric >= previous) {
      currentSession.performance[
        name
      ] = numeric;

      currentSession.performance_worst[
        name
      ] = {
        value: numeric,
        path:
          currentPathWithQuery(),
      };
    }
  }

  function setupPerformanceTracking() {
    try {
      const nav =
        performance.getEntriesByType?.(
          'navigation'
        )?.[0];

      if (nav) {
        updatePerformanceMetric(
          'ttfb_ms',
          Math.round(
            Math.max(
              0,
              nav.responseStart
            )
          )
        );
      }

      const paints =
        performance.getEntriesByType?.(
          'paint'
        ) || [];

      const fcp =
        paints.find(
          (entry) =>
            entry.name ===
            'first-contentful-paint'
        );

      if (fcp) {
        updatePerformanceMetric(
          'fcp_ms',
          Math.round(
            fcp.startTime
          )
        );
      }

      if (
        typeof PerformanceObserver ===
        'undefined'
      ) {
        return;
      }

      try {
        const lcpObserver =
          new PerformanceObserver(
            (list) => {
              list
                .getEntries()
                .forEach(
                  (entry) =>
                    updatePerformanceMetric(
                      'lcp_ms',
                      Math.round(
                        entry.startTime ||
                          0
                      )
                    )
                );
            }
          );

        lcpObserver.observe({
          type:
            'largest-contentful-paint',
          buffered: true,
        });
      } catch (_) {
        // unsupported
      }

      try {
        let pageCls = 0;

        const clsObserver =
          new PerformanceObserver(
            (list) => {
              list
                .getEntries()
                .forEach(
                  (entry) => {
                    if (
                      !entry.hadRecentInput
                    ) {
                      pageCls +=
                        Number(
                          entry.value ||
                            0
                        );
                    }
                  }
                );

              updatePerformanceMetric(
                'cls',
                Number(
                  pageCls.toFixed(3)
                )
              );
            }
          );

        clsObserver.observe({
          type: 'layout-shift',
          buffered: true,
        });
      } catch (_) {
        // unsupported
      }

      try {
        const inpObserver =
          new PerformanceObserver(
            (list) => {
              list
                .getEntries()
                .forEach(
                  (entry) => {
                    if (
                      Number(
                        entry.interactionId ||
                          0
                      ) > 0
                    ) {
                      updatePerformanceMetric(
                        'inp_ms',
                        Math.round(
                          entry.duration ||
                            0
                        )
                      );
                    }
                  }
                );
            }
          );

        inpObserver.observe({
          type: 'event',
          buffered: true,
          durationThreshold: 40,
        });
      } catch (_) {
        // unsupported
      }
    } catch (_) {
      // Performance es diagnóstico opcional
      // y nunca debe romper el journey.
    }
  }

  function setupLifecycle() {
    window.setInterval(
      tickActiveTime,
      HEARTBEAT_MS
    );

    window.addEventListener(
      'scroll',
      monitorScroll,
      {
        passive: true,
      }
    );

    document.addEventListener(
      'visibilitychange',
      () => {
        tickActiveTime();

        if (
          document.visibilityState ===
          'hidden'
        ) {
          syncAllToFirebase();
          syncCartAttributes(
            true
          );
        }
      }
    );

    window.addEventListener(
      'pagehide',
      () => {
        tickActiveTime();

        currentSession.ended_at =
          nowIso();

        currentSession.page_exit_active_seconds =
          Math.round(
            (nowMs() -
              pageViewStartedAt) /
              1000
          );

        saveState();

        syncAllToFirebase();

        syncCartAttributes(
          true
        );
      }
    );
  }

  async function init() {
    updateTouches();

    currentSession =
      getCurrentSession();

    touchSession();
    saveState();

    installFetchMonitor();

    listenForNativeCartForms();

    listenForProductOptions();

    listenForCommerceSignals();

    document.addEventListener(
      'click',
      recordClick,
      true
    );

    setupPerformanceTracking();

    setupLifecycle();

    trackErrors();

    trackExitIntent();

    recordPageView();

    detectPurchasePage();

    window.setTimeout(
      () =>
        inspectProductAvailability(
          'load'
        ),
      250
    );

    cartSnapshot =
      await readCart();

    if (cartSnapshot) {
      currentSession.cart =
        cartSnapshot;

      currentSession.peak_cart_total =
        Math.max(
          Number(
            currentSession.peak_cart_total ||
              0
          ),
          Number(
            cartSnapshot.total_price ||
              0
          )
        );

      currentSession.peak_cart_items =
        Math.max(
          Number(
            currentSession.peak_cart_items ||
              0
          ),
          Number(
            cartSnapshot.item_count ||
              0
          )
        );

      saveState();
    }

    // Firebase desactivado:
    // el journey se conserva en
    // el navegador y en el pedido.
    scheduleCartSync();

    window.BE_Journey = {
      version: VERSION,

      getState: () =>
        typeof structuredClone ===
        'function'
          ? structuredClone(state)
          : JSON.parse(
              JSON.stringify(state)
            ),

      getReport: () =>
        cartAttributes(),

      getConclusion: () =>
        customerConclusionText(),

      getDiagnosis: () =>
        executiveCustomerSummary(),

      getPerformance: () => ({
        ...(
          currentSession.performance ||
          {}
        ),
      }),

      track: (
        type,
        details
      ) =>
        trackEvent(
          type,
          details
        ),

      syncFirebase:
        syncAllToFirebase,

      syncCart:
        syncCartAttributes,

      reset: () => {
        localStorage.removeItem(
          STORAGE_KEY
        );

        sessionStorage.removeItem(
          PENDING_CLICKS_KEY
        );

        window.location.reload();
      },
    };

    log('Inicializado', {
      visitor:
        state.visitor_id,
      session:
        currentSession.id,
    });
  }

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      init,
      {
        once: true,
      }
    );
  } else {
    init();
  }
})();